import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { auth, db, storage } from '../firebaseConfig';
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { firestore } from '../firebaseConfig'; // adjust path if needed
import { toast, ToastContainer } from 'react-toastify';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faImages } from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';
import {
  collection,
  query,
  doc,
  getDoc, 
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  onSnapshot,
  addDoc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import './Notes.css';
import PageInfoButton from '../components/PageInfoButton';
import { saveToGallery, formatSaveMessage } from '../utils/saveToGallery';

const SHARE_CONFIRMATION_MESSAGES = {
  noteOrganization: "Add this note to your organization's Fieldbook? Your private note stays separate. Other users in your organization may add their own interactions.",
  noteGlobal: 'Share this note globally as a read-only Fieldbook snapshot? Edits to your private note will update the global snapshot.',
  personOrganization: "Add this person's current interactions to your organization's Fieldbook? Your private person card stays separate, and later private changes will not alter the organization record.",
  personGlobal: 'Share this person card globally as a read-only Fieldbook snapshot? Its private identity and interaction changes will update the global snapshot.',
};

const SHARE_WARNING_CONTENT = {
  global: {
    heading: 'SHARING GLOBALLY — READ BEFORE CONTINUING',
    opening: 'This information will become searchable and readable by all authorized BlueWire users.',
    responsibility: 'You are responsible for the accuracy and lawful disclosure of the information you contribute.',
  },
  organization: {
    heading: 'SHARING WITH YOUR ORGANIZATION — READ BEFORE CONTINUING',
    opening: 'This information will become available to authorized members of your organization.',
    responsibility: 'You are responsible for your own contribution, not later interactions added by other officers.',
  },
};

function ShareConfirmationModal({
  confirmation,
  pending,
  onCancel,
  onConfirm,
  cancelButtonRef,
}) {
  const warning = SHARE_WARNING_CONTENT[confirmation.scope];

  return (
    <div className="note-modal notes-share-confirmation-modal">
      <section
        className="note-form notes-share-confirmation-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notes-share-confirmation-heading"
      >
        <div className="notes-share-confirmation-body">
          <h2 id="notes-share-confirmation-heading" className="notes-share-confirmation-heading">
            {warning.heading}
          </h2>
          <p>{warning.opening}</p>
          <p>
            Share only information personally observed first-hand or independently gathered
            through permitted means.
          </p>
          <p>
            <strong className="notes-share-confirmation-emphasis">NEVER</strong> include
            restricted information obtained from CJIS/NCIC, CAD, RMS, DMV, or other government
            databases.
          </p>
          <p>{warning.responsibility}</p>
          <p>BlueWire does not independently verify or endorse officer-authored content.</p>
          <p className="notes-share-confirmation-closing">
            Sharing restricted law-enforcement information may violate applicable laws, agency
            policies, or confidentiality requirements.
          </p>
          <p className="notes-share-confirmation-message">{confirmation.message}</p>
        </div>
        <div className="notes-share-confirmation-actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="notes-share-confirmation-cancel"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="notes-share-confirmation-submit"
            disabled={pending}
            aria-busy={pending}
            onClick={onConfirm}
          >
            {pending ? 'Sharing...' : 'Acknowledge & Share'}
          </button>
        </div>
      </section>
    </div>
  );
}




export default function Notes({ sidebarVisible }) {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [personRecords, setPersonRecords] = useState([]);
  const [filteredPersonRecords, setFilteredPersonRecords] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Person');
  const [addNoteStep, setAddNoteStep] = useState('chooseType');
  const [showFacialSearchOptions, setShowFacialSearchOptions] = useState(false);
  const [faceSearchPickerOpen, setFaceSearchPickerOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [personModalVisible, setPersonModalVisible] = useState(false);
  const [personCreateStep, setPersonCreateStep] = useState('method');
  const [personCreateMethod, setPersonCreateMethod] = useState('');
  const [personDraftId, setPersonDraftId] = useState('');
  const [personTempPortraitPath, setPersonTempPortraitPath] = useState('');
  const [personProfilePreview, setPersonProfilePreview] = useState('');
  const [idPortraitCandidates, setIdPortraitCandidates] = useState([]);
  const [personNarrativeDraft, setPersonNarrativeDraft] = useState('');
  const [isSavingPerson, setIsSavingPerson] = useState(false);
  const [isProcessingPersonImage, setIsProcessingPersonImage] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [editingPersonId, setEditingPersonId] = useState('');
  const [editingPersonData, setEditingPersonData] = useState(null);
  const [detailsPersonId, setDetailsPersonId] = useState('');
  const [detailsInteractionId, setDetailsInteractionId] = useState('');
  const [detailsIsDraft, setDetailsIsDraft] = useState(false);
  const [detailsNarrative, setDetailsNarrative] = useState('');
  const [detailsAssociates, setDetailsAssociates] = useState([]);
  const [detailsPhotoUrls, setDetailsPhotoUrls] = useState([]);
  const [detailsPhotoPaths, setDetailsPhotoPaths] = useState([]);
  const [detailsOriginalPhotoPaths, setDetailsOriginalPhotoPaths] = useState([]);
  const [detailsFiles, setDetailsFiles] = useState([]);
  const [detailsPreviewUrls, setDetailsPreviewUrls] = useState([]);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [creatingInteractionForId, setCreatingInteractionForId] = useState('');
  const [deletingInteractionId, setDeletingInteractionId] = useState('');
  const [faceSearchPreview, setFaceSearchPreview] = useState('');
  const [faceSearchResults, setFaceSearchResults] = useState([]);
  const [selectedFaceMergeIds, setSelectedFaceMergeIds] = useState([]);
  const [expandedFaceResultIds, setExpandedFaceResultIds] = useState([]);
  const [faceSearchMessage, setFaceSearchMessage] = useState('');
  const [isFaceSearching, setIsFaceSearching] = useState(false);
  const [editingFaceControlsForId, setEditingFaceControlsForId] = useState('');
  const [updatingFaceForId, setUpdatingFaceForId] = useState('');
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeSurvivorId, setMergeSurvivorId] = useState('');
  const [mergeSelections, setMergeSelections] = useState({});
  const [mergeKnownAssociates, setMergeKnownAssociates] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [tab, setTab] = useState('Person');
  const [ownerName, setOwnerName] = useState("");
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [editModeId, setEditModeId] = useState(null); 
  const [displayName, setDisplayName] = useState('');
  const [userRank, setUserRank] = useState('');
  const [userDepartment, setUserDepartment] = useState('');
  const [userAgency, setUserAgency] = useState('');
  const [userLoaded, setUserLoaded] = useState(false); // add this
  const [editedNoteData, setEditedNoteData] = useState(null); // local changes
  const [editedFiles, setEditedFiles] = useState([]);      // new files to upload
const [editedPreviewUrls, setEditedPreviewUrls] = useState([]);  // local preview URLs
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editNoteData, setEditNoteData] = useState(null); // full note object
  const [userState, setUserState] = useState('');
  const [userId, setUserId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
   const [deletingNoteId, setDeletingNoteId] = useState("");
   const [sharingNoteId, setSharingNoteId] = useState("");
   const [shareMenuForId, setShareMenuForId] = useState("");
   const [shareConfirmation, setShareConfirmation] = useState(null);
   const [isShareConfirmationPending, setIsShareConfirmationPending] = useState(false);
   const shareConfirmationCancelRef = useRef(null);
   const [retentionWarningRecord, setRetentionWarningRecord] = useState(null);
  const [retentionExtendRecord, setRetentionExtendRecord] = useState(null);
  const [isUpdatingRetention, setIsUpdatingRetention] = useState(false);

  useEffect(() => {
    if (!shareMenuForId) return undefined;

    const closeShareMenuOnOutsideTap = (event) => {
      if (event.target?.closest?.('.note-share-menu-wrap')) return;
      setShareMenuForId('');
    };

    document.addEventListener('pointerdown', closeShareMenuOnOutsideTap);
    return () => {
      document.removeEventListener('pointerdown', closeShareMenuOnOutsideTap);
    };
   }, [shareMenuForId]);

   useEffect(() => {
     if (!shareConfirmation) return undefined;

     if (!isShareConfirmationPending) {
       shareConfirmationCancelRef.current?.focus();
     }
     const closeOnEscape = (event) => {
       if (event.key === 'Escape' && !isShareConfirmationPending) {
         event.preventDefault();
         setShareConfirmation(null);
       }
     };

     document.addEventListener('keydown', closeOnEscape);
     return () => document.removeEventListener('keydown', closeOnEscape);
   }, [shareConfirmation, isShareConfirmationPending]);


  


  const EMPTY_PERSON = {
  firstName: '', middleName: '', lastName: '',
  dob: '', age: '', race: '', sex: '',
  streetAddress: '', city: '', addressState: '', addressZip: '',
  dl: '', dlState: '', phone: '',
  knownAssociates: [], gangAffiliated: false, gangName: '',
  narrative: ''
};

const EMPTY_PERSON_RECORD = {
  firstName: '', middleName: '', lastName: '',
  dob: '', state: '', race: '', sex: '',
  address: '', addressCity: '', addressState: '', addressZip: '',
  dl: '', dlState: '', phone: '',
  knownAssociates: []
};

const PERSON_RACE_OPTIONS = [
  '',
  'Black',
  'White',
  'Hispanic / Latino',
  'Asian',
  'American Indian / Alaska Native',
  'Native Hawaiian / Pacific Islander',
  'Middle Eastern / North African',
  'Other'
];

const PERSON_SEX_OPTIONS = ['', 'Male', 'Female'];

const PERSON_RECORD_FIELDS = [
  { key: 'firstName', label: 'FIRST NAME', autoCapitalize: 'words' },
  { key: 'middleName', label: 'MIDDLE NAME', autoCapitalize: 'words' },
  { key: 'lastName', label: 'LAST NAME', autoCapitalize: 'words' },
  { key: 'dob', label: 'DATE OF BIRTH', placeholder: 'MM/DD/YYYY', inputMode: 'numeric', maxLength: 10 },
  { key: 'state', label: 'STATE', autoCapitalize: 'words' },
  { key: 'race', label: 'RACE', type: 'select', options: PERSON_RACE_OPTIONS },
  { key: 'sex', label: 'SEX', type: 'select', options: PERSON_SEX_OPTIONS },
  { key: 'address', label: 'ADDRESS', autoCapitalize: 'words' },
  { key: 'addressCity', label: 'ADDRESS CITY', autoCapitalize: 'words' },
  { key: 'addressState', label: 'ADDRESS STATE', autoCapitalize: 'words' },
  { key: 'addressZip', label: 'ADDRESS ZIP', inputMode: 'numeric', maxLength: 12 },
  { key: 'dl', label: 'DL', autoCapitalize: 'characters' },
  { key: 'dlState', label: 'DL STATE', autoCapitalize: 'words' },
  { key: 'phone', label: 'PHONE', inputMode: 'tel' }
];

const EMPTY_VEHICLE = {
  make: '', model: '', plate: '', plateState: '',
  color: '', owner: '', narrative: ''
};

const EMPTY_PLACE = {
  streetAddress: '',          // street
  addressCity: '',      // city
  addressState: '',     // state
  addressZip: '',       // zip
  owner: '',
  occupants: [],
  narrative: ''
};

  const [personForm, setPersonForm] = useState(EMPTY_PERSON);
  const [personRecordForm, setPersonRecordForm] = useState(EMPTY_PERSON_RECORD);
  const [vehicleForm, setVehicleForm] = useState(EMPTY_VEHICLE);
  const [placeForm, setPlaceForm] = useState(EMPTY_PLACE);

  const makeClientId = (prefix) => {
    const randomPart =
      typeof crypto !== 'undefined' && crypto.randomUUID ?
        crypto.randomUUID() :
        `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    return `${prefix}_${randomPart}`.replace(/[^A-Za-z0-9_-]/g, '_');
  };

  const privateNotesCollection = () => collection(db, 'privateNotes', userId, 'notes');
  const privateNoteDoc = (noteId) => doc(db, 'privateNotes', userId, 'notes', noteId);
  const privatePersonRecordsCollection = () => collection(db, 'privateNotes', userId, 'personRecords');
  const privatePersonDoc = (personId) => doc(db, 'privateNotes', userId, 'personRecords', personId);
  const privatePersonInteractionsCollection = (personId) =>
    collection(db, 'privateNotes', userId, 'personRecords', personId, 'interactions');
  const privatePersonInteractionDoc = (personId, interactionId) =>
    doc(db, 'privateNotes', userId, 'personRecords', personId, 'interactions', interactionId);

  const getPersonName = (person) =>
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ') || 'Unnamed person';

  const formatTimestamp = (value) => {
    if (!value) return '';
    const date =
      value.seconds ? new Date(value.seconds * 1000) :
      value.toDate ? value.toDate() :
      new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
  };

  const retentionDate = (value) => {
    if (!value) return null;
    const date =
      value.seconds ? new Date(value.seconds * 1000) :
      value.toDate ? value.toDate() :
      new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const retentionMillis = (value) => {
    const date = retentionDate(value);
    return date ? date.getTime() : 0;
  };

  const isRetentionExpired = (record) => {
    const expiresAt = retentionMillis(record?.expiresAt);
    return expiresAt > 0 && expiresAt <= Date.now();
  };

  const getRetentionDaysRemaining = (record) => {
    const expiresAt = retentionMillis(record?.expiresAt);
    if (!expiresAt) return null;
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 86400000));
  };

  const isRetentionExpiring = (record) => {
    const days = getRetentionDaysRemaining(record);
    return days !== null && days > 0 && days <= 30;
  };

  const warningCycleMatches = (record) =>
    retentionMillis(record?.lastRetentionWarningCycleExpiresAt) ===
    retentionMillis(record?.expiresAt);

  const shouldShowRetentionWarning = (record) =>
    isRetentionExpiring(record) &&
    Number(record?.retentionWarningAcknowledgedCount || 0) < 2 &&
    !warningCycleMatches(record);

  const updateRetentionRecordLocal = (recordType, recordId, updates) => {
    if (recordType === 'personRecord') {
      setPersonRecords((current) => current.map((person) => (
        person.id === recordId ? { ...person, ...updates } : person
      )));
      return;
    }
    setNotes((current) => current.map((note) => (
      note.id === recordId ? { ...note, ...updates } : note
    )));
  };

  const getRetentionRecordLabel = (record) =>
    record?.recordType === 'personRecord' ? getPersonName(record) : `${record?.type || 'Note'} note`;

  const renderRetentionNotice = (record) => {
    const daysRemaining = getRetentionDaysRemaining(record);
    if (daysRemaining === null || daysRemaining > 30) return null;
    const recordType = record.recordType || 'note';
    return (
      <div className="retention-notice" onClick={(event) => event.stopPropagation()}>
        <div>
          <strong>{daysRemaining === 0 ? 'Expires today' : `${daysRemaining} days until deletion`}</strong>
          <span>Private notes expire three years after the record date unless extended.</span>
        </div>
        <button
          type="button"
          className="retention-extend-btn"
          onClick={(event) => {
            event.stopPropagation();
            setRetentionExtendRecord({ ...record, recordType });
          }}
        >
          Extend
        </button>
      </div>
    );
  };

  const buildSearchText = (person, interactions = []) => {
    const values = [
      person.firstName, person.middleName, person.lastName, person.dob,
      person.state, person.race, person.sex, person.address, person.addressCity,
      person.addressState, person.addressZip, person.dl, person.dlState,
      person.phone
    ];
    if (Array.isArray(person.knownAssociates)) {
      values.push(...person.knownAssociates);
    }
    interactions.forEach((interaction) => {
      values.push(interaction.narrative);
      if (Array.isArray(interaction.knownAssociates)) {
        values.push(...interaction.knownAssociates);
      }
    });
    return values.filter(Boolean).join(' ').toLowerCase();
  };

  const cleanAssociates = (associates) => Array.from(new Set(
    (Array.isArray(associates) ? associates : [])
      .map((assoc) => (assoc || '').toString().trim())
      .filter(Boolean)
  )).slice(0, 50);

  const revokePreviewUrls = (urls = []) => {
    urls.forEach((url) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetDetailsModal = () => {
    revokePreviewUrls(detailsPreviewUrls);
    setDetailsPersonId('');
    setDetailsInteractionId('');
    setDetailsIsDraft(false);
    setDetailsNarrative('');
    setDetailsAssociates([]);
    setDetailsPhotoUrls([]);
    setDetailsPhotoPaths([]);
    setDetailsOriginalPhotoPaths([]);
    setDetailsFiles([]);
    setDetailsPreviewUrls([]);
  };

  const uploadPersonTempImage = async (file, draftId, label) => {
    const storageRef = ref(storage, `personTemp/${userId}/${draftId}/${label}-${Date.now()}-${file.name}`);
    await uploadBytes(storageRef, file);
    return storageRef.fullPath;
  };

  const withCacheBust = (url) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  };

  const handleAdjustPersonTempPortrait = async (action) => {
    if (!personTempPortraitPath || isProcessingPersonImage) return;
    setIsProcessingPersonImage(true);
    try {
      const functionsInstance = getFunctions();
      const adjustPersonTempPortrait = httpsCallable(functionsInstance, 'adjustPersonTempPortrait');
      const result = await adjustPersonTempPortrait({
        tempPortraitPath: personTempPortraitPath,
        action
      });
      const nextPath = result.data?.tempPortraitPath || '';
      setPersonTempPortraitPath(nextPath);
      if (nextPath) {
        const nextUrl = await getDownloadURL(ref(storage, nextPath));
        setPersonProfilePreview(withCacheBust(nextUrl));
      } else {
        setPersonProfilePreview('');
      }
    } catch (err) {
      console.error('Unable to adjust person portrait:', err);
      toast.error(err?.message || 'Unable to adjust person image.');
    } finally {
      setIsProcessingPersonImage(false);
    }
  };

  const renderPersonRecordFields = (formData, setFormData) => (
    PERSON_RECORD_FIELDS.map((field) => {
      const value = formData?.[field.key] || '';
      const handleChange = (rawValue) => {
        const nextValue = field.key === 'dob' ? formatDOB(rawValue) : rawValue;
        setFormData((current) => ({ ...current, [field.key]: nextValue }));
      };

      if (field.type === 'select') {
        const options = field.options || [];
        const safeOptions = value && !options.includes(value) ? [...options, value] : options;
        return (
          <label key={field.key} className="person-field-label">
            {field.label}
            <select value={value} onChange={(e) => handleChange(e.target.value)}>
              {safeOptions.map((option) => (
                <option key={option || 'blank'} value={option}>
                  {option || `Select ${field.label ? field.label.toLowerCase() : ''}`.trim()}
                </option>
              ))}
            </select>
          </label>
        );
      }

      return (
        <label key={field.key} className="person-field-label">
          {field.label}
          <input
            value={value}
            placeholder={field.placeholder || field.label}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            autoCapitalize={field.autoCapitalize || 'sentences'}
            onChange={(e) => handleChange(e.target.value)}
          />
        </label>
      );
    })
  );

  const renderAssociateEditor = (associates, setAssociates) => (
    <div className="interaction-associates-editor person-associates-editor">
      <strong>Known Associates:</strong>
      {(associates || []).map((assoc, index) => (
        <div key={index} className="associate-row">
          <input
            value={assoc}
            placeholder={`Associate #${index + 1}`}
            onChange={(e) => {
              const updated = [...(associates || [])];
              updated[index] = e.target.value;
              setAssociates(updated);
            }}
          />
          <button
            type="button"
            onClick={() => setAssociates((associates || []).filter((_, idx) => idx !== index))}
          >
            x
          </button>
        </div>
      ))}
      <button
        type="button"
        className="add-associate-btn"
        onClick={() => setAssociates([...(associates || []), ''])}
      >
        + Add Associate
      </button>
    </div>
  );



	// Load notes from cache ONLY after userId is known
useEffect(() => {
  if (!userId) {
    setNotes([]);
    setFilteredNotes([]);
    return;
  }

  const cached = localStorage.getItem(`cachedNotes_${userId}`);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      setNotes(parsed);
      setFilteredNotes(parsed);
    } catch (err) {
      console.error('Failed to parse cached notes:', err);
    }
  } else {
    setNotes([]);       // ensure blank for new users
    setFilteredNotes([]);
  }
}, [userId]);


  
  const fetchNotes = async () => {
    if (!userId) return;
  try {
    const q = query(privateNotesCollection(), where('ownerId', '==', userId));
    const snapshot = await getDocs(q);
    const userNotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    userNotes.sort((a, b) => {
      const timeA = a.timestamp?.seconds || 0;
      const timeB = b.timestamp?.seconds || 0;
      return timeB - timeA;
    });

    setNotes(userNotes);
    setFilteredNotes(userNotes);
    localStorage.setItem(`cachedNotes_${userId}`, JSON.stringify(userNotes));
localStorage.setItem(`lastNotesFetch_${userId}`, Date.now().toString());
  } catch (error) {
    console.error("Error fetching notes:", error);
  }
};

const fetchPersonRecords = async () => {
  if (!userId) return;
  try {
    const q = query(privatePersonRecordsCollection(), where('ownerId', '==', userId));
    const snapshot = await getDocs(q);
    const records = await Promise.all(snapshot.docs.map(async (personDoc) => {
      const data = { id: personDoc.id, ...personDoc.data() };
      let profilePhotoUrl = data.profilePhotoUrl || '';
      if (!profilePhotoUrl && data.profilePhotoPath) {
        try {
          profilePhotoUrl = withCacheBust(
            await getDownloadURL(ref(storage, data.profilePhotoPath))
          );
        } catch (err) {
          console.warn('Unable to load person profile photo:', err);
        }
      } else if (profilePhotoUrl) {
        profilePhotoUrl = withCacheBust(profilePhotoUrl);
      }
      const interactionsSnap = await getDocs(
        query(
          privatePersonInteractionsCollection(personDoc.id),
          where('ownerId', '==', userId),
          orderBy('createdAt', 'desc')
        )
      );
      const interactions = interactionsSnap.docs.map((interactionDoc) => ({
        id: interactionDoc.id,
        ...interactionDoc.data()
      }));
      return {
        ...data,
        profilePhotoUrl,
        interactions,
        searchText: data.searchText || buildSearchText(data, interactions)
      };
    }));

    records.sort((a, b) => {
      const timeA = a.mostRecentInteractionAt?.seconds || a.createdAt?.seconds || 0;
      const timeB = b.mostRecentInteractionAt?.seconds || b.createdAt?.seconds || 0;
      return timeB - timeA;
    });
    setPersonRecords(records);
    setFilteredPersonRecords(records);
  } catch (error) {
    console.error("Error fetching person records:", error);
  }
};

  const highlightMatch = (text, searchTerm) => {
  const value = Array.isArray(text) ? text.join(', ') : (text ?? '').toString();
  const term = (searchTerm || '').toString().trim();
  if (!value || !term) return value;

  const lower = value.toLowerCase();
  const lowerTerm = term.toLowerCase();
  if (!lower.includes(lowerTerm)) return value;

  const parts = [];
  let cursor = 0;
  let matchIndex = lower.indexOf(lowerTerm, cursor);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(value.slice(cursor, matchIndex));
    }
    parts.push(
      <mark key={`${matchIndex}-${parts.length}`}>
        {value.slice(matchIndex, matchIndex + term.length)}
      </mark>
    );
    cursor = matchIndex + term.length;
    matchIndex = lower.indexOf(lowerTerm, cursor);
  }

  if (cursor < value.length) {
    parts.push(value.slice(cursor));
  }

  return <>{parts}</>;
};



 useEffect(() => {
  if (!userId) return;

  const lastFetch = localStorage.getItem(`lastNotesFetch_${userId}`);
  const now = Date.now();
  const fourDays = 4 * 24 * 60 * 60 * 1000;

  if (!lastFetch || now - parseInt(lastFetch) > fourDays) {
    fetchNotes();
  }
  fetchPersonRecords();
}, [userId]);



  
 useEffect(() => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    setOwnerName(user.displayName || user.email || "Unknown");
  }
}, []);


  // Fetch user info from Firestore
useEffect(() => {
  const unsubscribe = onAuthStateChanged(getAuth(), async (currentUser) => {
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setDisplayName(data.displayName || currentUser.email || '');
          setUserRank(data.rank || '');
          setUserDepartment(data.agency || '');
	  setUserState(data.state || '');
        } else {
          console.warn('User profile not found in Firestore.');
        }
	setUserId(currentUser.uid);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    } else {
      console.warn('No authenticated user found.');
    }

    // ✅ Always set true after attempt
    setUserLoaded(true);
  });

  return () => unsubscribe(); // cleanup
}, []);




  useEffect(() => {
    const filtered = notes.filter(note => {
      if (isRetentionExpired(note)) return false;
      const allValues = Object.values(note).join(' ').toLowerCase();
      return allValues.includes(search.toLowerCase());
    });
    setFilteredNotes(filtered);
    const personFiltered = personRecords.filter(person => {
      if (isRetentionExpired(person)) return false;
      const haystack = person.searchText || buildSearchText(person, person.interactions || []);
      return haystack.includes(search.toLowerCase());
    });
    setFilteredPersonRecords(personFiltered);
  }, [search, notes, personRecords]);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 ? age : '';
  };



	// --- content validators ---
const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');
const cleanedArray = (arr) =>
  (Array.isArray(arr) ? arr.map(s => (s ?? '').toString().trim()).filter(Boolean) : []);

// Only count meaningful, user-entered fields for Person notes
function isPersonNoteEmpty(n) {
  const assoc = cleanedArray(n.knownAssociates);
  return (
    isBlank(n.firstName) &&
    isBlank(n.middleName) &&
    isBlank(n.lastName) &&
    isBlank(n.dob) &&
    isBlank(n.race) &&
    isBlank(n.sex) &&
    isBlank(n.streetAddress || n.address || '') && // tolerate either key
    isBlank(n.city || n.addressCity || '') &&
    isBlank(n.addressState) &&
    isBlank(n.addressZip) &&
    isBlank(n.dl) &&
    isBlank(n.dlState) &&
    isBlank(n.phone) &&
    isBlank(n.narrative) &&
    assoc.length === 0
  );
}



  const handleAddNote = async () => {
  if (isCreatingNote) return;
  const noteData =
    tab === 'Person' ? personForm :
    tab === 'Vehicle' ? vehicleForm :
    placeForm;

  if (!userLoaded) {
    console.warn("User data not loaded yet.");
    alert("Please wait for your profile to load before saving.");
    return;
  }

  // generic guard (kept as-is, but now Person also gets a stricter check below)
  const hasContent = Object.values(noteData).some(
    val => (Array.isArray(val) ? val.length > 0 : val?.toString().trim() !== "")
  );
  if (!hasContent) {
    alert("The note is empty.");
    return;
  }

  // ✅ Normalize person note fields
  if (tab === 'Person') {
    noteData.gangAffiliated = noteData.gangAffiliated || false;
    noteData.gangName = noteData.gangAffiliated ? noteData.gangName || '' : '';

    if (typeof noteData.knownAssociates === 'string') {
      noteData.knownAssociates = noteData.knownAssociates.split(',').map(s => s.trim());
    }
    // 🔒 minimal addition: drop blank entries so they don’t count as content
    if (Array.isArray(noteData.knownAssociates)) {
      noteData.knownAssociates = noteData.knownAssociates
        .map(s => (s ?? '').toString().trim())
        .filter(Boolean);
    } else {
      noteData.knownAssociates = [];
    }

    // 🔒 run the stricter “Person is empty” check ONLY for Person
    if (isPersonNoteEmpty(noteData)) {
      alert('Please fill at least one field before saving a Person note.');
      return;
    }
  }
	// ✅ Upload selected photos first (if any)
setIsCreatingNote(true);
let photoUrls = [];

if (selectedFiles.length > 0) {
  try {
    const uploaded = await Promise.all(
      selectedFiles.map(async (file) => {
        const storageRef = ref(storage, `notes/${userId}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      })
    );
    photoUrls = uploaded;
  } catch (err) {
    console.error("Error uploading photos:", err);
    toast.error("Photo upload failed for one or more files.");
  }
}

  const fullNote = {
    ...noteData,
    type: tab,
    timestamp: new Date(),
    ownerId: userId,
    ownerName: displayName,
    rank: userRank,
    agency: userAgency,
    entryState: userState || '',  // keep as-is
    flags: [],                    // keep as-is
    photoUrls, // ✅ store uploaded image URLs
    address: noteData.streetAddress || '',
    addressCity: noteData.city || '',
    addressState: noteData.addressState || '',
    addressZip: noteData.addressZip || ''
  };

  try {
    setIsCreatingNote(true);
    const docRef = await addDoc(privateNotesCollection(), fullNote);
    const savedNote = { ...fullNote, id: docRef.id };

    localStorage.setItem(`cachedNotes_${userId}`, JSON.stringify([...notes, savedNote]));

    setPersonForm(EMPTY_PERSON);
    setVehicleForm(EMPTY_VEHICLE);
    setPlaceForm(EMPTY_PLACE);
    setModalVisible(false);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setTab('Person');
    fetchNotes();
  } catch (error) {
    console.error("Error adding note:", error);
  } finally {
    setIsCreatingNote(false);
  }
};




  const formatDOB = (value) => {
  const digits = value.replace(/\D/g, ''); // remove non-digits
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);

  let formatted = mm;
  if (dd) formatted += '/' + dd;
  if (yyyy) formatted += '/' + yyyy;

  return formatted;
};



 // eslint-disable-next-line no-unused-vars
 const handleLegacyDatabaseShareNote = async (note) => {
  const confirmed = window.confirm("Share this note to the Database?");
  if (!confirmed) return;

  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error("You must be logged in to share a note.");
      return;
    }

    // ✅ Load user profile
    const userRef = doc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);

    let ownerName = currentUser.email || "Unknown";
    let rank = "";
    let agency = "";
    let state = userState || "";

    if (userSnap.exists()) {
      const userData = userSnap.data();
      ownerName = userData.firstInitialLastName || userData.displayName || currentUser.email || "Unknown";
      rank = userData.rank || "";
      agency = userData.agency || "";
      state = userData.state || state;
    }

    // ✅ Normalize fields for Person and Place
    if (note.type === "Person") {
      note.gangAffiliated = note.gangAffiliated || false;
      note.gangName = note.gangAffiliated ? note.gangName || '' : '';
      if (typeof note.knownAssociates === 'string') {
        note.knownAssociates = note.knownAssociates.split(',').map(s => s.trim());
      }
      note.knownAssociates = note.knownAssociates || [];
    }

    if (note.type === "Place") {
      if (typeof note.occupants === 'string') {
        note.occupants = note.occupants.split(',').map(s => s.trim());
      }
      note.occupants = note.occupants || [];
      note.streetAddress = note.streetAddress || '';
      note.narrative = note.narrative || '';
    }

    // ✅ Duplicate attachments into /sharedNotes/
    const duplicateAttachmentsForSharedNote = async (originalNote, userId) => {
      if (!originalNote.photoUrls || originalNote.photoUrls.length === 0) return [];

      const newUrls = await Promise.all(
        originalNote.photoUrls.map(async (url) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();

            const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
            const fileRef = ref(storage, `sharedNotes/${userId}/${filename}`);
            await uploadBytes(fileRef, blob);
            const newUrl = await getDownloadURL(fileRef);
            return newUrl;
          } catch (err) {
            console.error("⚠️ Failed to duplicate file:", err);
            return null;
          }
        })
      );

      return newUrls.filter(Boolean);
    };

    const newPhotoUrls = await duplicateAttachmentsForSharedNote(note, currentUser.uid);

    // ✅ Prepare data for shared note
    const sharedData = {
      ...note,
      type: note.type || tab,
      timestamp: serverTimestamp(),
      ownerId: currentUser.uid,
      ownerName,
      rank,
      agency,
      entryState: state,
      flags: note.flags || [],
      photoUrls: newPhotoUrls, // use duplicated copies
    };

    // ✅ Create or overwrite in sharedNotes
    if (!note.id) {
      toast.error("Cannot share note: missing note ID.");
      return;
    }

    const sharedDocRef = doc(db, 'sharedNotes', note.id);
    await setDoc(sharedDocRef, sharedData);

    toast.success('✅ Note successfully shared to the database!');
  } catch (error) {
    console.error("❌ Error sharing note:", error);
    toast.error('Failed to share note. Please try again.');
  }
};



 const openShareConfirmation = (scope, message, onConfirm) => {
   if (sharingNoteId || shareConfirmation || isShareConfirmationPending) return;
   setShareConfirmation({ scope, message, onConfirm });
 };

 const cancelShareConfirmation = () => {
   if (!isShareConfirmationPending) setShareConfirmation(null);
 };

 const confirmShareConfirmation = async () => {
   if (!shareConfirmation || isShareConfirmationPending || sharingNoteId) return;

   setIsShareConfirmationPending(true);
   try {
     await shareConfirmation.onConfirm();
   } finally {
     setIsShareConfirmationPending(false);
     setShareConfirmation(null);
   }
 };

 const handleShareNote = async (note) => {
   if (!note?.id || sharingNoteId) return;

  try {
    setSharingNoteId(note.id);
    const share = httpsCallable(getFunctions(), 'addPrivateNoteToOrganizationFieldbook');
    const result = await share({ noteId: note.id });
    if (result.data?.status === 'possibleMatches') {
      const candidateText = (result.data.candidates || [])
        .map((item, index) => `${index + 1}. ${item.title}`)
        .join('\n');
      const createSeparate = window.confirm(
        `Possible existing Fieldbook record found:\n\n${candidateText}\n\nCreate a separate Fieldbook record anyway?`
      );
      if (!createSeparate) return;
      await share({ noteId: note.id, createSeparate: true });
    }
    toast.success('Note added to Organization Fieldbook.');
    await fetchNotes();
  } catch (error) {
    console.error("Error adding note to Fieldbook:", error);
    toast.error(error?.message || 'Failed to add note to Fieldbook.');
  } finally {
    setSharingNoteId("");
  }
};

const handleGlobalFieldbookShare = async (note) => {
  if (!note?.id || sharingNoteId) return;
  const type = String(note.type || '').toLowerCase();
  if (type === 'place') {
    toast.error('Place notes can be shared only with your organization.');
    return;
  }
  const isShared = note.globalShare?.active === true;
  if (isShared) {
    const confirmed = window.confirm(
      'This note is already shared globally. Stop sharing it globally? Your private note will remain.'
    );
    if (!confirmed) return;
  }

  try {
    setSharingNoteId(note.id);
    const callableName = isShared ? 'unsharePrivateNoteGlobally' : 'sharePrivateNoteGlobally';
    const share = httpsCallable(getFunctions(), callableName);
    await share({ noteId: note.id });
    toast.success(isShared ? 'Global Fieldbook share removed.' : 'Note shared globally.');
    await fetchNotes();
  } catch (error) {
    console.error("Global Fieldbook share failed:", error);
    toast.error(error?.message || 'Global Fieldbook share failed.');
  } finally {
    setSharingNoteId("");
  }
};

const handleSharePersonRecordToOrganization = async (person) => {
  if (!person?.id || sharingNoteId) return;

  try {
    setSharingNoteId(person.id);
    const share = httpsCallable(
      getFunctions(),
      'addPrivatePersonRecordToOrganizationFieldbook'
    );
    let result = await share({ personId: person.id });
    if (result.data?.status === 'possibleMatches') {
      const candidates = result.data.candidates || [];
      const candidateText = candidates
        .map((item, index) => `${index + 1}. ${item.title}`)
        .join('\n');
      const selection = window.prompt(
        `Possible existing Fieldbook records found:\n\n${candidateText}\n\nEnter a record number to add these interactions there, enter 0 to create a separate record, or Cancel to stop.`
      );
      if (selection === null) return;
      const selectedNumber = Number.parseInt(selection, 10);
      if (selectedNumber === 0) {
        result = await share({ personId: person.id, createSeparate: true });
      } else if (selectedNumber >= 1 && selectedNumber <= candidates.length) {
        result = await share({
          personId: person.id,
          targetRecordId: candidates[selectedNumber - 1].id,
        });
      } else {
        toast.error('Select one of the listed records or enter 0.');
        return;
      }
    }
    toast.success(
      result.data?.status === 'alreadyShared'
        ? 'This person record is already in Organization Fieldbook.'
        : 'Person record added to Organization Fieldbook.'
    );
    await fetchPersonRecords();
  } catch (error) {
    console.error('Person record organization share failed:', error);
    toast.error(error?.message || 'Failed to add this person record to Fieldbook.');
  } finally {
    setSharingNoteId('');
  }
};

const handleGlobalPersonRecordShare = async (person) => {
  if (!person?.id || sharingNoteId) return;
  const isShared = person.globalShare?.active === true;
  if (isShared) {
    const confirmed = window.confirm(
      'This person record is already shared globally. Stop sharing it globally? Your private person card will remain.'
    );
    if (!confirmed) return;
  }

  try {
    setSharingNoteId(person.id);
    const callableName = isShared
      ? 'unsharePrivatePersonRecordGlobally'
      : 'sharePrivatePersonRecordGlobally';
    const share = httpsCallable(getFunctions(), callableName);
    await share({ personId: person.id });
    toast.success(
      isShared
        ? 'Global Fieldbook share removed.'
        : 'Person record shared globally.'
    );
    await fetchPersonRecords();
  } catch (error) {
    console.error('Global person record share failed:', error);
    toast.error(error?.message || 'Global Fieldbook share failed.');
  } finally {
    setSharingNoteId('');
  }
};

  const handleWebShareNote = async (note) => {
  let content = `Note Type: ${note.type}\n`;

  if (note.type === 'Person') {
    content += `Name: ${note.firstName || ''} ${note.middleName || ''} ${note.lastName || ''}\n`;
    content += `DOB: ${note.dob} (Age: ${note.age || ''})\n`;
    content += `Race/Sex: ${note.race}/${note.sex}\n`;
    content += `Address: ${note.address}, ${note.addressState}\n`;
    content += `DL: ${note.dl} (${note.dlState})\n`;
    content += `Phone: ${note.phone}\n`;
    content += `Narrative: ${note.narrative || ''}\n`;
  } else if (note.type === 'Vehicle') {
    content += `Make/Model: ${note.make} ${note.model}\n`;
    content += `Plate/State: ${note.plate} / ${note.plateState}\n`;
    content += `Color: ${note.color}\n`;
    content += `Owner: ${note.owner}\n`;
    content += `Narrative: ${note.narrative || ''}\n`;
  } else if (note.type === 'Place') {
    content += `Address: ${note.address}\n`;
    content += `Owner: ${note.owner}\n`;
    content += `Occupants: ${(note.occupants || []).join(', ')}\n`;
    content += `Narrative: ${note.narrative || ''}\n`;
  }

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Shared Note',
        text: content
      });
    } catch (err) {
      console.error('Sharing failed:', err);
    }
  } else {
    try {
      await navigator.clipboard.writeText(content);
      alert('Note copied to clipboard!');
    } catch (err) {
      console.error('Clipboard write failed:', err);
      alert('Unable to copy the note.');
    }
  }
};


  const handleWebSharePersonRecord = async (person) => {
  const interactionLines = (person.interactions || []).map((interaction, index) => {
    const narrative = interaction.narrative || 'No narrative entered.';
    const associates = Array.isArray(interaction.knownAssociates) && interaction.knownAssociates.length > 0 ?
      `\n  Known Associates: ${interaction.knownAssociates.join(', ')}` :
      '';
    return `Interaction ${index + 1}: ${formatTimestamp(interaction.createdAt)}\n  Narrative: ${narrative}${associates}`;
  });

  const content = [
    'Note Type: Person',
    `Name: ${[person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ') || 'N/A'}`,
    `DOB: ${person.dob || 'N/A'}`,
    `State: ${person.state || 'N/A'}`,
    `Race/Sex: ${person.race || ''}/${person.sex || ''}`,
    `Address: ${[person.address, person.addressCity, person.addressState, person.addressZip].filter(Boolean).join(', ') || 'N/A'}`,
    `DL: ${person.dl || ''}${person.dlState ? ` (${person.dlState})` : ''}`,
    `Phone: ${person.phone || 'N/A'}`,
    `Source: ${person.source === 'scanned_id' ? 'Scanned ID' : person.faceSearchEnabled ? 'Person photograph' : 'Text only'}`,
    `Card Created: ${formatTimestamp(person.createdAt)}`,
    `Most Recent Interaction: ${formatTimestamp(person.mostRecentInteractionAt)}`,
    '',
    'Interactions:',
    interactionLines.length ? interactionLines.join('\n\n') : 'No interactions entered.'
  ].join('\n');

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Shared Person Note',
        text: content
      });
    } catch (err) {
      console.error('Sharing failed:', err);
    }
  } else {
    try {
      await navigator.clipboard.writeText(content);
      alert('Person note copied to clipboard!');
    } catch (err) {
      console.error('Clipboard write failed:', err);
      alert('Unable to copy the person note.');
    }
  }
};



    const handleTabChange = (newTab) => {
  setTab(newTab);

 if (newTab === 'Person') {
  setVehicleForm(EMPTY_VEHICLE);
  setPlaceForm(EMPTY_PLACE);
} else if (newTab === 'Vehicle') {
  setPersonForm(EMPTY_PERSON);
  setPlaceForm(EMPTY_PLACE);
} else { // Place
  setPersonForm(EMPTY_PERSON);
  setVehicleForm(EMPTY_VEHICLE);
}}




   const handleEditNote = (note) => {
  setEditModeId(note.id);
  setEditedNoteData({ ...note });
};


const handleSaveInlineEdit = async () => {
  if (isSavingNote) return;
  try {
    setIsSavingNote(true);
    const { id, ...data } = editedNoteData;

    // ✅ Normalize knownAssociates
    if (data.type === 'Person') {
      if (typeof data.knownAssociates === 'string') {
        data.knownAssociates = data.knownAssociates.split(',').map(s => s.trim());
      }
      data.knownAssociates = data.knownAssociates || [];

      // ✅ Gang fields normalization
      data.gangAffiliated = data.gangAffiliated || false;
      data.gangName = data.gangAffiliated ? data.gangName || '' : '';
    }

    // ✅ Ensure entryState is present
    if (!data.entryState) {
      data.entryState = userState || '';
    }

    // ✅ Ensure flags exist
    if (!data.flags) {
      data.flags = [];
    }

    // ✅ Upload new attachments (if any)
    if (editedFiles.length > 0) {
      try {
        const uploaded = await Promise.all(
          editedFiles.map(async (file) => {
            const storageRef = ref(storage, `notes/${userId}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            return await getDownloadURL(snapshot.ref);
          })
        );

        // Append new URLs to existing ones
        data.photoUrls = [...(data.photoUrls || []), ...uploaded];
      } catch (err) {
        console.error("Error uploading new attachments:", err);
      }
    }

    // ✅ Save updated note
    const noteRef = privateNoteDoc(id);
    await updateDoc(noteRef, {
      ...data,
      editedAt: new Date(),
    });

    fetchNotes();
    setEditModeId(null);
    setEditedFiles([]);
    setEditedPreviewUrls([]);
    setEditedNoteData(null);
  } catch (err) {
    console.error("Error updating note:", err);
    alert("Failed to update note.");
  } finally {
    setIsSavingNote(false);
  }
};





const handleDeleteNote = async (id) => {
  if (deletingNoteId) return;
  console.log("Deleting note with ID:", id);
  if (!window.confirm("Are you sure you want to delete this note?")) return;

  try {
    setDeletingNoteId(id);
    const noteRef = privateNoteDoc(id);
    await deleteDoc(noteRef);

    await fetchNotes(); // 🔄 Refresh to reflect deletion
  } catch (err) {
    console.error("Error deleting note:", err);
    alert("Failed to delete note.");
  } finally {
    setDeletingNoteId("");
  }
};

const openAddPerson = () => {
  setPersonDraftId(makeClientId('person'));
  setPersonCreateStep('review');
  setPersonCreateMethod('');
  setPersonRecordForm(EMPTY_PERSON_RECORD);
  setPersonNarrativeDraft('');
  setPersonTempPortraitPath('');
  setPersonProfilePreview('');
  setIdPortraitCandidates([]);
  setShowFacialSearchOptions(false);
};

const openAddNote = () => {
  setAddNoteStep('chooseType');
  setTab('Person');
  setModalVisible(true);
};

const chooseAddNoteType = (type) => {
  handleTabChange(type);
  setSelectedCategory(type);
  setAddNoteStep('form');
  if (type === 'Person') {
    openAddPerson();
  }
};

const handlePersonMethod = (method) => {
  if (idPortraitCandidates.length > 0) {
    cleanupIdPortraitCandidates(idPortraitCandidates);
  }
  setPersonCreateMethod(method);
  setPersonCreateStep('image');
  setIdPortraitCandidates([]);
};

const cleanupIdPortraitCandidates = async (candidatesToClean = []) => {
  await Promise.all(candidatesToClean.map(async (candidate) => {
    if (!candidate?.tempPortraitPath) return;
    try {
      await deleteObject(ref(storage, candidate.tempPortraitPath));
    } catch (err) {
      console.warn('Unable to delete unused ID portrait candidate:', err);
    }
  }));
};

const handlePersonImageSelect = async (event) => {
  const file = event.target.files?.[0];
  if (!file || !userId || !personDraftId) return;
  const localPreview = URL.createObjectURL(file);
  setPersonProfilePreview(localPreview);
  setIdPortraitCandidates([]);
  setIsProcessingPersonImage(true);
  try {
    const imagePath = await uploadPersonTempImage(file, personDraftId, personCreateMethod === 'scanned_id' ? 'id' : 'face');
    const functionsInstance = getFunctions();
    if (personCreateMethod === 'scanned_id') {
      const preparePersonIdScan = httpsCallable(functionsInstance, 'preparePersonIdScan');
      const result = await preparePersonIdScan({ draftId: personDraftId, imagePath });
      const { fields, tempPortraitPath, needsPortraitSelection, portraitCandidates } = result.data || {};
      setPersonRecordForm((current) => ({
        ...current,
        firstName: fields?.firstName || current.firstName,
        middleName: fields?.middleName || current.middleName,
        lastName: fields?.lastName || current.lastName,
        dob: fields?.dob || current.dob,
        state: fields?.state || current.state,
        address: fields?.address || current.address,
        addressCity: fields?.addressCity || current.addressCity,
        addressState: fields?.addressState || current.addressState,
        addressZip: fields?.addressZip || current.addressZip,
        dl: fields?.dl || current.dl,
        dlState: fields?.dlState || current.dlState
      }));
      if (needsPortraitSelection && Array.isArray(portraitCandidates) && portraitCandidates.length > 0) {
        const candidatesWithUrls = await Promise.all(portraitCandidates.map(async (candidate) => ({
          ...candidate,
          previewUrl: withCacheBust(await getDownloadURL(ref(storage, candidate.tempPortraitPath)))
        })));
        setIdPortraitCandidates(candidatesWithUrls);
        setPersonTempPortraitPath('');
        setPersonProfilePreview('');
        setPersonCreateStep('selectPortrait');
        return;
      }
      setPersonTempPortraitPath(tempPortraitPath || '');
      if (tempPortraitPath) {
        const cropUrl = await getDownloadURL(ref(storage, tempPortraitPath));
        setPersonProfilePreview(withCacheBust(cropUrl));
      }
    } else {
      const validatePersonFace = httpsCallable(functionsInstance, 'validatePersonFace');
      await validatePersonFace({ imagePath });
      setPersonTempPortraitPath(imagePath);
    }
    setPersonCreateStep('review');
  } catch (err) {
    console.error('Person image processing failed:', err);
    toast.error(err?.message || 'Unable to process the selected image.');
  } finally {
    setIsProcessingPersonImage(false);
  }
};

const handleSelectIdPortraitCandidate = (candidate) => {
  if (!candidate?.tempPortraitPath || isProcessingPersonImage) return;
  cleanupIdPortraitCandidates(idPortraitCandidates.filter((item) =>
    item.tempPortraitPath !== candidate.tempPortraitPath
  ));
  setPersonTempPortraitPath(candidate.tempPortraitPath);
  setPersonProfilePreview(candidate.previewUrl || '');
  setIdPortraitCandidates([]);
  setPersonCreateStep('review');
};

const handleBackFromIdPortraitSelection = () => {
  cleanupIdPortraitCandidates(idPortraitCandidates);
  setIdPortraitCandidates([]);
  setPersonProfilePreview('');
  setPersonCreateStep('image');
};

const handleSavePersonRecord = async () => {
  if (isSavingPerson || !personDraftId) return;
  setIsSavingPerson(true);
  try {
    let photoUrls = [];
    let photoPaths = [];
    if (selectedFiles.length > 0) {
      const cappedFiles = selectedFiles.slice(0, 3);
      const uploaded = await Promise.all(cappedFiles.map(async (file) => {
        const storageRef = ref(storage, `personRecords/${userId}/${personDraftId}/attachments/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return {
          url: await getDownloadURL(snapshot.ref),
          path: snapshot.ref.fullPath
        };
      }));
      photoUrls = uploaded.map((item) => item.url);
      photoPaths = uploaded.map((item) => item.path);
    }
    const functionsInstance = getFunctions();
    const savePersonRecord = httpsCallable(functionsInstance, 'savePersonRecord');
    await savePersonRecord({
      draftId: personDraftId,
      personId: personDraftId,
      interactionId: `${personDraftId}_first`,
      source: personTempPortraitPath ? personCreateMethod : 'text_only',
      tempPortraitPath: personTempPortraitPath || '',
      person: personRecordForm,
      narrative: personNarrativeDraft,
      knownAssociates: cleanAssociates(personRecordForm.knownAssociates),
      photoUrls,
      photoPaths
    });
    setPersonModalVisible(false);
    setModalVisible(false);
    setAddNoteStep('chooseType');
    setPersonCreateStep('method');
    setPersonCreateMethod('');
    setPersonDraftId('');
    setPersonTempPortraitPath('');
    setPersonProfilePreview('');
    setPersonRecordForm(EMPTY_PERSON_RECORD);
    setPersonNarrativeDraft('');
    setSelectedFiles([]);
    setPreviewUrls([]);
    setSelectedCategory('Person');
    await fetchPersonRecords();
  } catch (err) {
    console.error('Save person failed:', err);
    toast.error(err?.message || 'Unable to save person record.');
  } finally {
    setIsSavingPerson(false);
  }
};

const handleFaceSearchSelect = async (event) => {
  const file = event.target.files?.[0];
  if (!file || !userId) return;
  const localPreview = URL.createObjectURL(file);
  const draftId = makeClientId('search');
  setFaceSearchPreview(localPreview);
  setFaceSearchResults([]);
  setFaceSearchMessage('');
  setIsFaceSearching(true);
  try {
    const imagePath = await uploadPersonTempImage(file, draftId, 'search');
    const functionsInstance = getFunctions();
    const searchOwnPersonFaces = httpsCallable(functionsInstance, 'searchOwnPersonFaces');
    const result = await searchOwnPersonFaces({ imagePath });
    const data = result.data || {};
    setFaceSearchMessage(data.message || '');
    if (data.status === 'ok') {
      const hydrated = await Promise.all((data.results || []).map(async (item) => {
        const localPerson = personRecords.find((person) => person.id === item.personId);
        let profilePhotoUrl = localPerson?.profilePhotoUrl || '';
        if (!profilePhotoUrl && item.person?.profilePhotoPath) {
          try {
            profilePhotoUrl = await getDownloadURL(ref(storage, item.person.profilePhotoPath));
          } catch (err) {
            console.warn('Unable to load face search profile photo:', err);
          }
        }
        return {
          ...item,
          person: {
          ...(localPerson || {}),
          ...(item.person || {}),
            id: item.personId,
            profilePhotoUrl
          },
          interactions: localPerson?.interactions || item.recentInteractions || []
        };
      }));
      setFaceSearchResults(hydrated);
      setSelectedFaceMergeIds(hydrated.map((result) => result.personId).filter(Boolean));
      setExpandedFaceResultIds([]);
      setSelectedCategory('Person');
      setFaceSearchPickerOpen(false);
    }
  } catch (err) {
    console.error('Face search failed:', err);
    toast.error(err?.message || 'Face search failed.');
  } finally {
    setIsFaceSearching(false);
    event.target.value = '';
  }
};

const clearFaceSearch = () => {
  if (faceSearchPreview && faceSearchPreview.startsWith('blob:')) {
    URL.revokeObjectURL(faceSearchPreview);
  }
  setFaceSearchPreview('');
  setFaceSearchResults([]);
  setSelectedFaceMergeIds([]);
  setExpandedFaceResultIds([]);
  setFaceSearchMessage('');
  setFaceSearchPickerOpen(false);
};

useEffect(() => {
  if (retentionWarningRecord) return;
  const expiringPeople = personRecords
    .filter((person) => shouldShowRetentionWarning(person))
    .map((person) => ({ ...person, recordType: 'personRecord' }));
  const expiringNotes = notes
    .filter((note) => shouldShowRetentionWarning(note))
    .map((note) => ({ ...note, recordType: 'note' }));
  const next = [...expiringPeople, ...expiringNotes]
    .sort((a, b) => retentionMillis(a.expiresAt) - retentionMillis(b.expiresAt))[0];
  if (next) {
    setRetentionWarningRecord(next);
  }
}, [notes, personRecords, retentionWarningRecord]);

const acknowledgeRetentionWarning = async (goToRecord = false) => {
  const record = retentionWarningRecord;
  if (!record || isUpdatingRetention) return;
  setIsUpdatingRetention(true);
  try {
    const acknowledgeRetention = httpsCallable(
      getFunctions(),
      'acknowledgePrivateNoteRetentionWarning'
    );
    await acknowledgeRetention({
      recordType: record.recordType,
      recordId: record.id
    });
    updateRetentionRecordLocal(record.recordType, record.id, {
      retentionWarningAcknowledgedCount: Number(record.retentionWarningAcknowledgedCount || 0) + 1,
      lastRetentionWarningCycleExpiresAt: record.expiresAt
    });
    setRetentionWarningRecord(null);
    if (goToRecord) {
      setSelectedCategory(record.recordType === 'personRecord' ? 'Person' : record.type);
      if (record.recordType === 'personRecord') {
        setSelectedPersonId(record.id);
      } else {
        setExpandedNoteId(record.id);
      }
      setTimeout(() => {
        document.getElementById(`retention-record-${record.recordType}-${record.id}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 150);
    }
  } catch (err) {
    console.error('Unable to acknowledge retention warning:', err);
    toast.error('Unable to update retention warning.');
  } finally {
    setIsUpdatingRetention(false);
  }
};

const confirmExtendRetention = async () => {
  const record = retentionExtendRecord;
  if (!record || isUpdatingRetention) return;
  setIsUpdatingRetention(true);
  try {
    const extendRetention = httpsCallable(getFunctions(), 'extendPrivateNoteRetention');
    const result = await extendRetention({
      recordType: record.recordType,
      recordId: record.id
    });
    const expiresAt = result?.data?.expiresAt;
    updateRetentionRecordLocal(record.recordType, record.id, {
      expiresAt,
      lastRetentionExtendedAt: new Date(),
      retentionExtensionCount: Number(record.retentionExtensionCount || 0) + 1
    });
    setRetentionExtendRecord(null);
    toast.success('Retention extended for 3 months.');
  } catch (err) {
    console.error('Unable to extend retention:', err);
    toast.error(err?.message || 'Unable to extend retention.');
  } finally {
    setIsUpdatingRetention(false);
  }
};

const toggleFaceResultExpanded = (personId) => {
  setExpandedFaceResultIds((current) => (
    current.includes(personId)
      ? current.filter((id) => id !== personId)
      : [...current, personId]
  ));
};

const handleSavePersonEdit = async () => {
  if (!editingPersonId || !editingPersonData) return;
  try {
    const personRef = privatePersonDoc(editingPersonId);
    const nextData = normalizePersonEdit(editingPersonData);
    await updateDoc(personRef, {
      ...nextData,
      searchText: buildSearchText(nextData, editingPersonData.interactions || []),
      updatedAt: serverTimestamp()
    });
    setEditingPersonId('');
    setEditingPersonData(null);
    await fetchPersonRecords();
  } catch (err) {
    console.error('Unable to update person:', err);
    toast.error('Unable to update person.');
  }
};

const normalizePersonEdit = (person) => ({
  firstName: person.firstName || '',
  middleName: person.middleName || '',
  lastName: person.lastName || '',
  dob: person.dob || '',
  state: person.state || '',
  race: person.race || '',
  sex: person.sex || '',
  address: person.address || '',
  addressCity: person.addressCity || '',
  addressState: person.addressState || '',
  addressZip: person.addressZip || '',
  dl: person.dl || '',
  dlState: person.dlState || '',
  phone: person.phone || '',
  knownAssociates: cleanAssociates(person.knownAssociates)
});

const openAddDetails = (person, interaction) => {
  const target = interaction || person.interactions?.[0];
  if (!target) return;
  setModalVisible(false);
  setEditModalVisible(false);
  setPersonModalVisible(false);
  setMergeModalVisible(false);
  setSelectedPersonId(person.id);
  setDetailsPersonId(person.id);
  setDetailsInteractionId(target.id);
  setDetailsIsDraft(false);
  setDetailsNarrative(target.narrative || '');
  setDetailsAssociates(target.knownAssociates || []);
  setDetailsPhotoUrls(target.photoUrls || []);
  setDetailsPhotoPaths(target.photoPaths || []);
  setDetailsOriginalPhotoPaths(target.photoPaths || []);
  setDetailsFiles([]);
  revokePreviewUrls(detailsPreviewUrls);
  setDetailsPreviewUrls([]);
};

const saveInteractionDetails = async () => {
  if (isSavingDetails || !detailsPersonId || !detailsInteractionId) return;
  setIsSavingDetails(true);
  try {
    const cleanedDetailsAssociates = cleanAssociates(detailsAssociates);
    if (detailsIsDraft) {
      const functionsInstance = getFunctions();
      const addPersonInteraction = httpsCallable(functionsInstance, 'addPersonInteraction');
      await addPersonInteraction({ personId: detailsPersonId, interactionId: detailsInteractionId });
    }
    const uploaded = await Promise.all(detailsFiles.map(async (file, index) => {
      const storageRef = ref(storage, `personRecords/${userId}/${detailsPersonId}/attachments/${Date.now()}_${index}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      return {
        url: await getDownloadURL(snapshot.ref),
        path: snapshot.ref.fullPath
      };
    }));
    const nextPhotoUrls = [
      ...detailsPhotoUrls,
      ...uploaded.map((item) => item.url)
    ];
    const nextPhotoPaths = [
      ...detailsPhotoPaths,
      ...uploaded.map((item) => item.path)
    ];
    const removedPhotoPaths = detailsOriginalPhotoPaths.filter((path) => path && !nextPhotoPaths.includes(path));
    const interactionRef = privatePersonInteractionDoc(detailsPersonId, detailsInteractionId);
    await updateDoc(interactionRef, {
      narrative: detailsNarrative,
      knownAssociates: cleanedDetailsAssociates,
      photoUrls: nextPhotoUrls,
      photoPaths: nextPhotoPaths,
      searchText: [detailsNarrative, ...cleanedDetailsAssociates].join(' ').toLowerCase(),
      updatedAt: serverTimestamp()
    });
    await Promise.all(removedPhotoPaths.map(async (path) => {
      try {
        await deleteObject(ref(storage, path));
      } catch (err) {
        console.warn('Unable to delete removed interaction attachment:', err);
      }
    }));
    const currentPerson = personRecords.find((person) => person.id === detailsPersonId);
    if (currentPerson) {
      const nextPersonAssociates = cleanAssociates([
        ...(currentPerson.knownAssociates || []),
        ...cleanedDetailsAssociates
      ]);
      const nextInteractions = (currentPerson.interactions || []).map((interaction) => (
        interaction.id === detailsInteractionId
          ? {
              ...interaction,
              narrative: detailsNarrative,
              knownAssociates: cleanedDetailsAssociates,
              photoUrls: nextPhotoUrls,
              photoPaths: nextPhotoPaths
            }
          : interaction
      ));
      if (!nextInteractions.some((interaction) => interaction.id === detailsInteractionId)) {
        nextInteractions.push({
          id: detailsInteractionId,
          narrative: detailsNarrative,
          knownAssociates: cleanedDetailsAssociates,
          photoUrls: nextPhotoUrls,
          photoPaths: nextPhotoPaths
        });
      }
      await updateDoc(privatePersonDoc(detailsPersonId), {
        knownAssociates: nextPersonAssociates,
        searchText: buildSearchText({ ...currentPerson, knownAssociates: nextPersonAssociates }, nextInteractions),
        updatedAt: serverTimestamp()
      });
    }
    resetDetailsModal();
    await fetchPersonRecords();
  } catch (err) {
    console.error('Unable to save interaction:', err);
    toast.error('Unable to save details.');
  } finally {
    setIsSavingDetails(false);
  }
};

const handleAddNewInteraction = async (person) => {
  const interactionId = makeClientId('interaction');
  setCreatingInteractionForId(person.id);
  setModalVisible(false);
  setEditModalVisible(false);
  setPersonModalVisible(false);
  setMergeModalVisible(false);
  setSelectedPersonId(person.id);
  setDetailsPersonId(person.id);
  setDetailsInteractionId(interactionId);
  setDetailsIsDraft(true);
  setDetailsNarrative('');
  setDetailsAssociates([]);
  setDetailsPhotoUrls([]);
  setDetailsPhotoPaths([]);
  setDetailsOriginalPhotoPaths([]);
  setDetailsFiles([]);
  revokePreviewUrls(detailsPreviewUrls);
  setDetailsPreviewUrls([]);
  setTimeout(() => setCreatingInteractionForId(''), 250);
};

const handleDeleteInteraction = async (person, interaction) => {
  if (!person?.id || !interaction?.id) return;
  const confirmed = window.confirm('Delete this interaction?');
  if (!confirmed) return;
  setDeletingInteractionId(interaction.id);
  try {
    const functionsInstance = getFunctions();
    const deletePersonInteraction = httpsCallable(functionsInstance, 'deletePersonInteraction');
    await deletePersonInteraction({ personId: person.id, interactionId: interaction.id });
    await fetchPersonRecords();
    toast.success('Interaction deleted.');
  } catch (err) {
    console.error('Unable to delete interaction:', err);
    toast.error('Unable to delete interaction.');
  } finally {
    setDeletingInteractionId('');
  }
};

const handleDeletePersonRecord = async (person) => {
  if (!person?.id) return;
  const confirmed = window.confirm(`Delete the entire ${getPersonName(person)} entry? This cannot be undone.`);
  if (!confirmed) return;

  try {
    await deleteDoc(privatePersonDoc(person.id));
    if (selectedPersonId === person.id) {
      setSelectedPersonId('');
    }
    await fetchPersonRecords();
    toast.success('Person record deleted.');
  } catch (err) {
    console.error('Unable to delete person:', err);
    toast.error('Unable to delete person record.');
  }
};

const handleUpdatePersonFaceSelect = async (person, event) => {
  const file = event.target.files?.[0];
  if (!file || !person?.id) return;
  setUpdatingFaceForId(person.id);
  try {
    const draftId = makeClientId('face');
    const imagePath = await uploadPersonTempImage(file, draftId, 'profile');
    const functionsInstance = getFunctions();
    const updatePersonFace = httpsCallable(functionsInstance, 'updatePersonFace');
    await updatePersonFace({
      personId: person.id,
      action: 'replace',
      faceRecordId: draftId,
      tempPortraitPath: imagePath
    });
    await fetchPersonRecords();
    setEditingFaceControlsForId('');
    toast.success('Facial search picture updated.');
  } catch (err) {
    console.error('Unable to update facial search picture:', err);
    toast.error(err?.message || 'Unable to update facial search picture.');
  } finally {
    setUpdatingFaceForId('');
    event.target.value = '';
  }
};

const handleUpdatePersonFaceAction = async (person, action) => {
  if (!person?.id || updatingFaceForId === person.id) return;
  setUpdatingFaceForId(person.id);
  try {
    const functionsInstance = getFunctions();
    const updatePersonFace = httpsCallable(functionsInstance, 'updatePersonFace');
    const result = await updatePersonFace({
      personId: person.id,
      action,
      faceRecordId: makeClientId('face')
    });
    const profilePhotoPath = result.data?.profilePhotoPath || '';
    if (profilePhotoPath) {
      const nextUrl = withCacheBust(await getDownloadURL(ref(storage, profilePhotoPath)));
      setPersonRecords((current) => current.map((item) => (
        item.id === person.id
          ? { ...item, profilePhotoPath, profilePhotoUrl: nextUrl, faceSearchEnabled: true }
          : item
      )));
      setFilteredPersonRecords((current) => current.map((item) => (
        item.id === person.id
          ? { ...item, profilePhotoPath, profilePhotoUrl: nextUrl, faceSearchEnabled: true }
          : item
      )));
    }
    await fetchPersonRecords();
    toast.success('Facial search picture updated.');
  } catch (err) {
    console.error('Unable to adjust facial search picture:', err);
    toast.error(err?.message || 'Unable to adjust facial search picture.');
  } finally {
    setUpdatingFaceForId('');
  }
};

const handleRemovePersonFace = async (person) => {
  if (!person?.id) return;
  const confirmed = window.confirm('Remove this facial search picture?');
  if (!confirmed) return;
  setUpdatingFaceForId(person.id);
  try {
    const functionsInstance = getFunctions();
    const updatePersonFace = httpsCallable(functionsInstance, 'updatePersonFace');
    await updatePersonFace({ personId: person.id, action: 'remove' });
    await fetchPersonRecords();
    setEditingFaceControlsForId('');
    toast.success('Facial search picture removed.');
  } catch (err) {
    console.error('Unable to remove facial search picture:', err);
    toast.error('Unable to remove facial search picture.');
  } finally {
    setUpdatingFaceForId('');
  }
};

const startMerge = (people) => {
  const candidates = (people || [])
    .map((person) => ({ ...person, id: person.id || person.personId }))
    .filter((person) => person.id);
  if (candidates.length < 2) return;
  setMergeCandidates(candidates);
  setMergeSurvivorId(candidates[0].id);
  const selections = {};
  ['firstName', 'middleName', 'lastName', 'dob', 'state', 'race', 'sex', 'address', 'addressCity', 'addressState', 'addressZip', 'dl', 'dlState', 'phone'].forEach((field) => {
    const values = Array.from(new Set(candidates.map((p) => p[field]).filter(Boolean)));
    selections[field] = values[0] || '';
  });
  setMergeKnownAssociates(cleanAssociates(candidates.flatMap((person) => person.knownAssociates || [])));
  setMergeSelections(selections);
  setMergeModalVisible(true);
};

const confirmMerge = async () => {
  if (isMerging || !mergeSurvivorId || mergeCandidates.length < 2) return;
  const candidateIds = mergeCandidates.map((person) => person.id).filter(Boolean);
  if (!candidateIds.includes(mergeSurvivorId)) {
    toast.error('Choose a record to keep before merging.');
    return;
  }
  const duplicateIds = candidateIds.filter((id) => id !== mergeSurvivorId);
  if (duplicateIds.length < 1) {
    toast.error('Select at least two records to merge.');
    return;
  }
  const survivorId = mergeSurvivorId;
  setIsMerging(true);
  try {
    const functionsInstance = getFunctions();
    const mergePersonRecords = httpsCallable(functionsInstance, 'mergePersonRecords');
    await mergePersonRecords({
      mergeId: makeClientId('merge'),
      survivorId,
      duplicateIds,
      selectedFields: {
        ...mergeSelections,
        knownAssociates: cleanAssociates(mergeKnownAssociates)
      }
    });
    setMergeModalVisible(false);
    setMergeCandidates([]);
    setMergeSurvivorId('');
    setMergeSelections({});
    setMergeKnownAssociates([]);
    clearFaceSearch();
    setSelectedCategory('Person');
    setSelectedPersonId(survivorId);
    await fetchPersonRecords();
    toast.success('Person records merged.');
  } catch (err) {
    console.error('Merge failed:', err);
    toast.error(err?.message || 'Unable to merge records.');
  } finally {
    setIsMerging(false);
  }
};




  const renderTabFields = () => {
    if (tab === 'Person') {
      return (
        <>
          <input
  type="text"
  placeholder="First Name"
  value={personForm.firstName || ''}
  onChange={e => setPersonForm(f => ({ ...f, firstName: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="Middle Name"
  value={personForm.middleName || ''}
  onChange={e => setPersonForm(f => ({ ...f, middleName: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="Last Name"
  value={personForm.lastName || ''}
  onChange={e => setPersonForm(f => ({ ...f, lastName: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="DOB (MM/DD/YYYY)"
  maxLength={10}
  value={personForm.dob || ''}
  onChange={e => {
    const formattedDOB = formatDOB(e.target.value);
    const age = calculateAge(formattedDOB);
    setPersonForm(f => ({ ...f, dob: formattedDOB, age }));
  }}
  style={{ fontSize: 16 }}
/>


<input
  type="text"
  placeholder="Race (e.g. B, W, A, U)"
  value={personForm.race || ''}
  onChange={e => setPersonForm(f => ({ ...f, race: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="Sex (e.g. M, F)"
  value={personForm.sex || ''}
  onChange={e => setPersonForm(f => ({ ...f, sex: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="Street Address"
  value={personForm.streetAddress || ""}
  onChange={(e) => setPersonForm({ ...personForm, streetAddress: e.target.value })}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="City"
  value={personForm.city || ""}
  onChange={(e) => setPersonForm({ ...personForm, city: e.target.value })}
  style={{ fontSize: 16 }}
/>


<input
  type="text"
  placeholder="State"
  value={personForm.addressState || ''}
  onChange={e => setPersonForm(f => ({ ...f, addressState: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  placeholder="Zip Code"
  value={personForm.addressZip || ''}
  onChange={e => setPersonForm(f => ({ ...f, addressZip: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="DL #"
  value={personForm.dl || ''}
  onChange={e => setPersonForm(f => ({ ...f, dl: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="DL State"
  value={personForm.dlState || ''}
  onChange={e => setPersonForm(f => ({ ...f, dlState: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  type="text"
  placeholder="Phone"
  value={personForm.phone || ''}
  onChange={e => setPersonForm(f => ({ ...f, phone: e.target.value }))}
  style={{ fontSize: 16 }}
/>

 <div style={{ marginTop: '12px' }}>
  <strong>Known Associates:</strong>
  <div style={{ marginTop: '6px', marginBottom: '10px' }}>
    <button
  className="add-associate-btn"
  onClick={() =>
    setPersonForm(f => ({
      ...f,
      knownAssociates: [...(f.knownAssociates || []), '']
    }))
  }
>
  + Add Associate
</button>

  </div>

  {(personForm.knownAssociates || []).map((assoc, index) => (
    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
      <input
        value={assoc}
        placeholder={`Associate #${index + 1}`}
        onChange={(e) => {
          const updated = [...personForm.knownAssociates];
          updated[index] = e.target.value;
          setPersonForm(f => ({ ...f, knownAssociates: updated }));
        }}
        style={{ flex: 1, marginRight: '6px' }}
      />
      <button
        onClick={() => {
          const updated = [...personForm.knownAssociates];
          updated.splice(index, 1);
          setPersonForm(f => ({ ...f, knownAssociates: updated }));
        }}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          color: 'red',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '16px'
        }}
        title="Remove"
      >
        ✖
      </button>
    </div>
  ))}
</div>

          <textarea className="note-textarea full-width" 
          placeholder="Narrative" 
          rows="5" 
          onChange={e => setPersonForm(f => ({ ...f, narrative: e.target.value }))}
          style={{ fontSize: 16 }}></textarea>
        </>
      );
    } else if (tab === 'Vehicle') {
      return (
        <>
          <input
  className="note-input"
  type="text"
  placeholder="Make"
  value={vehicleForm.make || ''}
  onChange={e => setVehicleForm(f => ({ ...f, make: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="Model"
  value={vehicleForm.model || ''}
  onChange={e => setVehicleForm(f => ({ ...f, model: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="Plate"
  value={vehicleForm.plate || ''}
  onChange={e => setVehicleForm(f => ({ ...f, plate: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="State"
  value={vehicleForm.plateState || ''}
  onChange={e => setVehicleForm(f => ({ ...f, plateState: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="Color"
  value={vehicleForm.color || ''}
  onChange={e => setVehicleForm(f => ({ ...f, color: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="Owner"
  value={vehicleForm.owner || ''}
  onChange={e => setVehicleForm(f => ({ ...f, owner: e.target.value }))}
  style={{ fontSize: 16 }}
/>

          <textarea className="note-textarea full-width" 
          placeholder="Narrative" 
          rows="5" 
          onChange={e => setVehicleForm(f => ({ ...f, narrative: e.target.value }))}
          style={{ fontSize: 16 }}></textarea>
        </>
      );
    } else {
      return (
        <>
        <input
  placeholder="Street Address"
  value={placeForm.streetAddress || ''}
  onChange={e => setPlaceForm(f => ({ ...f, streetAddress: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  placeholder="City"
  value={placeForm.addressCity}
  onChange={e => setPlaceForm(f => ({ ...f, addressCity: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  placeholder="State"
  value={placeForm.addressState}
  onChange={e => setPlaceForm(f => ({ ...f, addressState: e.target.value }))}
  style={{ fontSize: 16 }}
/>
<input
  placeholder="Zip Code"
  value={placeForm.addressZip}
  onChange={e => setPlaceForm(f => ({ ...f, addressZip: e.target.value }))}
  style={{ fontSize: 16 }}
/>

<input
  className="note-input"
  type="text"
  placeholder="Owner"
  value={placeForm.owner || ''}
  onChange={e => setPlaceForm(f => ({ ...f, owner: e.target.value }))}
  style={{ fontSize: 16 }}
/>


{placeForm.occupants?.map((occ, i) => (
  <input
    key={i}
    className="note-input"
    type="text"
    placeholder={`Occupant #${i + 1}`}
    value={occ || ''}
    onChange={e => {
      const updated = [...placeForm.occupants];
      updated[i] = e.target.value;
      setPlaceForm(f => ({ ...f, occupants: updated }));
    }}
    style={{ fontSize: 16 }}
  />
))}

<button
  type="button"
  onClick={() =>
    setPlaceForm(f => ({
      ...f,
      occupants: [...(f.occupants || []), '']
    }))
  }
>
  + Add Occupant
</button>

          <textarea className="note-textarea full-width" 
          placeholder="Narrative" 
          rows="5" 
          onChange={e => setPlaceForm(f => ({ ...f, narrative: e.target.value }))}
          style={{ fontSize: 16 }}></textarea>
        </>
      );
    }
  };



return (
  <div className={`notes-page ${!sidebarVisible ? 'compact' : ''}`}>
  <div className="page-info-header-block">
    <h2 style={{ marginBottom: '16px', marginTop: '0px', fontSize: '28px', fontWeight: 'bold', textAlign: 'center' }}>Notes</h2>
    <div className="page-info-button-wrapper">
      <PageInfoButton title="About Notes">
        <p>Capture field intel about people, vehicles, and places in one workspace</p>
        <ul className="page-info-list">
          <li><strong> Entering Information:</strong> Click "Add New Note," choose the relevant tab (person, vehicle, or place) and enter the information. There are no required fields, as long as some information is entered the note will save.</li>
          <li><strong>Person records:</strong> Person notes save as private person cards with timestamped interactions. You can add details immediately or return later by opening an interaction timestamp.</li>
          <li><strong>Facial search:</strong> On the Person tab, tap Face Search to take a photo or choose one from your gallery. BlueWire checks that image against person records saved by your own account and returns possible facial matches with similarity scores, saved details, and recent interaction timestamps.</li>
          <li><strong>Facial matching records:</strong> A person record becomes searchable only when you add a facial-search picture. You can add that picture from a person photograph or from an ID scan, then rotate, replace, or remove it before saving.</li>
          <li><strong>ID extraction:</strong> When you add a person by ID, BlueWire reads available ID fields into editable person fields and extracts the ID portrait for facial search. If more than one portrait may be usable, you choose the main portrait before approving and saving the record.</li>
          <li><strong>Private by default:</strong> Your notes are visible only to you. To share a note, open it, tap Share, and choose the application you want to use.</li>
          <li><strong>Expiration:</strong> New private notes expire three years after the note date. New person records expire three years after the most recent interaction. When a note is within 30 days of expiration, BlueWire shows a countdown and an Extend button for adding three more months.</li>
          <li><strong>Search + highlight:</strong> The search bar filters as you type and highlights matches inside every section so you can jump straight to the right record.</li>
          <li><strong>Offline friendly:</strong> Your notes are stored locally on your device speed and offline access, and also securely backed up in the cloud. If you are using a new device and don't see your notes, tap the <strong>refresh</strong> (🔄) button  to sync.</li>
<li><strong>Security Reminder:</strong> <u> Local device storage may be accessible to system administrators (e.g., IT departments).
If you are using a shared or non-secured workstation, clear the local cache after logging out to protect sensitive information.</u></li>
        </ul>
      </PageInfoButton>
    </div>
  </div>

  <p className="notes-description">
  These are your personal notes. They are not visible to anyone else unless you choose to share them.
  If you don't see your notes, press here{' '}
  <button
    onClick={fetchNotes}
    className="notes-refresh-btn"
    title="Force reload notes from Firestore"
  >
    🔄
  </button>.
</p>

<div className="records-toggle category-tabs">
  <button
    className={selectedCategory === 'Person' ? 'active' : ''}
    onClick={() => setSelectedCategory('Person')}
  >
    Person
  </button>
  <button
    className={selectedCategory === 'Vehicle' ? 'active' : ''}
    onClick={() => setSelectedCategory('Vehicle')}
  >
    Vehicle
  </button>
  <button
    className={selectedCategory === 'Place' ? 'active' : ''}
    onClick={() => setSelectedCategory('Place')}
  >
    Place
  </button>
</div>

   <div className="search-bar-container">
  <input
    type="text"
    className="search-bar"
    placeholder="Search notes..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
  />
  {selectedCategory === 'Person' && (
    <div className="face-search-actions">
      {!faceSearchPickerOpen ? (
        <button
          type="button"
          className="face-search-btn"
          disabled={isFaceSearching}
          onClick={() => setFaceSearchPickerOpen(true)}
        >
          {isFaceSearching ? 'Searching...' : 'Face Search'}
        </button>
      ) : (
        <>
          <label className={`face-search-btn ${isFaceSearching ? 'disabled' : ''}`}>
            {isFaceSearching ? 'Searching...' : 'Take Photo'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={isFaceSearching}
              onChange={handleFaceSearchSelect}
              hidden
            />
          </label>
          <label className={`face-search-btn ${isFaceSearching ? 'disabled' : ''}`}>
            Choose from Gallery
            <input
              type="file"
              accept="image/*"
              disabled={isFaceSearching}
              onChange={handleFaceSearchSelect}
              hidden
            />
          </label>
          <button
            type="button"
            className="person-secondary-btn"
            disabled={isFaceSearching}
            onClick={() => setFaceSearchPickerOpen(false)}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  )}
</div>

{faceSearchPreview && selectedCategory === 'Person' && (
  <div className="face-search-panel">
    <div className="face-search-toolbar">
      {faceSearchMessage && <div className="face-search-message">{faceSearchMessage}</div>}
      <button className="person-secondary-btn" onClick={clearFaceSearch}>Clear Face Search</button>
    </div>
    {isFaceSearching && (
      <div className="face-search-active-preview">
        <img src={faceSearchPreview} alt="Face search reference" />
        <div>
          <strong>Searching your records...</strong>
        </div>
      </div>
    )}
    {!isFaceSearching && faceSearchResults.length === 0 && !faceSearchMessage && (
      <div className="face-search-empty">No matching results found.</div>
    )}
    {faceSearchResults.length > 1 && (
      <button
        className="person-secondary-btn"
        disabled={selectedFaceMergeIds.length < 2}
        onClick={() => startMerge(
          faceSearchResults
            .filter((result) => selectedFaceMergeIds.includes(result.personId))
            .map((result) => ({
              ...(result.person || {}),
              id: result.personId
            }))
        )}
      >
        Merge Records
      </button>
    )}
    {faceSearchResults.map((result) => {
      const faceResultExpanded = expandedFaceResultIds.includes(result.personId);
      const resultInteractions = result.interactions || result.recentInteractions || [];
      return (
      <div className="face-search-result" key={result.personId}>
        {faceSearchResults.length > 1 && (
          <label className="face-merge-checkbox" onClick={(event) => event.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedFaceMergeIds.includes(result.personId)}
              onChange={(event) => {
                const checked = event.target.checked;
                setSelectedFaceMergeIds((current) => (
                  checked
                    ? Array.from(new Set([...current, result.personId]))
                    : current.filter((id) => id !== result.personId)
                ));
              }}
            />
            Merge this match
          </label>
        )}
        <div className="face-compare-row">
          {result.person.profilePhotoUrl && <img src={result.person.profilePhotoUrl} alt="Saved profile" />}
          <span className="face-compare-symbol" aria-hidden="true">&lt;-&gt;</span>
          <img src={faceSearchPreview} alt="Search preview" />
        </div>
        <div>
          <strong>
            Possible match
            {typeof result.similarity === 'number' ? ` (${result.similarity.toFixed(1)}% similarity)` : ''}
          </strong>
          <div className="face-result-name">{getPersonName(result.person)}</div>
          <div>DOB: {result.person.dob || 'N/A'}</div>
          <div>State: {result.person.state || 'N/A'}</div>
          <div>{result.person.interactionCount || result.interactions?.length || 0} Previous Interactions</div>
          {resultInteractions.slice(0, 4).map((interaction) => (
            <button
              key={interaction.id}
              className="timestamp-link"
              type="button"
              onClick={() => {
                if (!faceResultExpanded) {
                  toggleFaceResultExpanded(result.personId);
                }
                setTimeout(() => {
                  document.getElementById(`face-result-interaction-${interaction.id}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }, 150);
              }}
            >
              {formatTimestamp(interaction.createdAt)}
            </button>
          ))}
          <button className="person-primary-btn" type="button" onClick={() => toggleFaceResultExpanded(result.personId)}>
            {faceResultExpanded ? 'Collapse Person' : 'View Person'}
          </button>
        </div>
        {faceResultExpanded && (
          <div className="person-card-details face-result-details">
            <div><strong>Source:</strong> {result.person.source === 'scanned_id' ? 'Scanned ID' : result.person.faceSearchEnabled ? 'Person photograph' : 'Text only'}</div>
            <div><strong>Age:</strong> {calculateAge(result.person.dob)}</div>
            <div><strong>Race/Sex:</strong> {highlightMatch(result.person.race || '', search)}/{highlightMatch(result.person.sex || '', search)}</div>
            <div><strong>Address:</strong> {highlightMatch([result.person.address, result.person.addressCity, result.person.addressState, result.person.addressZip].filter(Boolean).join(', '), search)}</div>
            <div><strong>DL:</strong> {highlightMatch(result.person.dl || '', search)} {highlightMatch(result.person.dlState || '', search)}</div>
            <div><strong>Phone:</strong> {highlightMatch(result.person.phone || '', search)}</div>
            {Array.isArray(result.person.knownAssociates) && result.person.knownAssociates.length > 0 && (
              <div>
                <strong>Known Associates:</strong>
                <ul className="person-associates-list">
                  {result.person.knownAssociates.map((associate, index) => (
                    <li key={`${associate}-${index}`}>{highlightMatch(associate, search)}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="interaction-history">
              <strong>Interactions:</strong>
              {resultInteractions.length > 0 ? (
                resultInteractions.map((interaction) => (
                  <div className="interaction-card" id={`face-result-interaction-${interaction.id}`} key={interaction.id}>
                    <button
                      className="timestamp-link"
                      type="button"
                      onClick={() => openAddDetails({ ...result.person, id: result.personId }, interaction)}
                    >
                      {formatTimestamp(interaction.createdAt)}
                    </button>
                    <div className="note-narrative-display">{interaction.narrative ? highlightMatch(interaction.narrative, search) : 'No narrative entered.'}</div>
                    {Array.isArray(interaction.knownAssociates) && interaction.knownAssociates.length > 0 && (
                      <div><strong>Known Associates:</strong> {highlightMatch(interaction.knownAssociates, search)}</div>
                    )}
                    {Array.isArray(interaction.photoUrls) && interaction.photoUrls.length > 0 && (
                      <div className="person-attachment-row">
                        {interaction.photoUrls.map((url, index) => (
                          <img
                            key={`${url}-${index}`}
                            src={url}
                            alt={`Person attachment ${index + 1}`}
                            onClick={() => setModalImage(url)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div>No interactions entered.</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
    })}
  </div>
)}

{selectedCategory === 'Person' && !faceSearchPreview && (
  <div className="people-list">
    {filteredPersonRecords.map((person) => {
      const expanded = selectedPersonId === person.id;
      const editing = editingPersonId === person.id;
      const sourceLabel = person.source === 'scanned_id' ?
        'Scanned ID' :
        person.faceSearchEnabled ? 'Person photograph' : 'Text only';
      const personAge = calculateAge(person.dob);
      return (
        <div
          className="person-card"
          id={`retention-record-personRecord-${person.id}`}
          key={person.id}
        >
          <div className="person-card-summary" onClick={() => setSelectedPersonId(expanded ? '' : person.id)}>
            <div className="person-summary-heading">
              <strong>{highlightMatch(getPersonName(person), search)}</strong>
              {(person.organizationContribution?.active === true ||
                person.globalShare?.active === true) && (
                <span className="fieldbook-share-badges">
                  {person.organizationContribution?.active === true && (
                    <span title="This person record has been contributed to your organization's Fieldbook.">
                      🏢
                    </span>
                  )}
                  {person.globalShare?.active === true && (
                    <span title="This person record is currently shared globally.">
                      🌐
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="person-profile-face-wrap">
              {person.profilePhotoUrl ? (
                <img className="person-profile-face" src={person.profilePhotoUrl} alt="Profile face" />
              ) : (
                <div className="person-profile-face placeholder">No Photo</div>
              )}
              {expanded && person.profilePhotoUrl && (
                <button
                  type="button"
                  className="person-photo-expand-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    setModalImage(person.profilePhotoUrl);
                  }}
                >
                  Enlarge
                </button>
              )}
            </div>
            <div className="person-summary-text">
              <span>
                DOB: {person.dob ? highlightMatch(person.dob, search) : 'N/A'} · State: {person.state ? highlightMatch(person.state, search) : 'N/A'}
              </span>
              <span>Age: {personAge}</span>
              <span>{person.interactionCount || person.interactions?.length || 0} interactions</span>
              {renderRetentionNotice({ ...person, recordType: 'personRecord' })}
            </div>
          </div>
          {expanded && (
            <div className="person-card-details">
              {editing ? (
                <div className="person-edit-grid">
                  <div className="person-face-edit-panel">
                    <div className="person-face-edit-header">
                      <button
                        type="button"
                        className="profile-photo-edit-trigger"
                        disabled={updatingFaceForId === person.id}
                        onClick={() => setEditingFaceControlsForId(editingFaceControlsForId === person.id ? '' : person.id)}
                      >
                        {person.profilePhotoUrl ? (
                          <img className="person-profile-preview" src={person.profilePhotoUrl} alt="Current facial search" />
                        ) : (
                          <div className="person-profile-preview placeholder">No Photo</div>
                        )}
                        <span className="photo-edit-badge">Edit</span>
                      </button>
                      <div className="person-face-edit-copy">
                        <strong>
                          {person.profilePhotoPath ? 'Replace Picture for Facial Search' : 'Add Picture for Facial Search'}
                        </strong>
                        <span>Tap the photo area to update the facial-search picture.</span>
                        {updatingFaceForId === person.id && <span className="face-search-message face-update-message">Updating picture...</span>}
                      </div>
                    </div>
                    {editingFaceControlsForId === person.id && (
                      <div className="compact-photo-actions">
                        {person.profilePhotoPath && (
                          <>
                            <button
                              type="button"
                              className="person-secondary-btn compact-action-btn"
                              disabled={updatingFaceForId === person.id}
                              onClick={() => handleUpdatePersonFaceAction(person, 'rotate_left')}
                            >
                              Rotate Left
                            </button>
                            <button
                              type="button"
                              className="person-secondary-btn compact-action-btn"
                              disabled={updatingFaceForId === person.id}
                              onClick={() => handleUpdatePersonFaceAction(person, 'rotate_right')}
                            >
                              Rotate Right
                            </button>
                            <button
                              type="button"
                              className="person-secondary-btn compact-action-btn"
                              disabled={updatingFaceForId === person.id}
                              onClick={() => handleUpdatePersonFaceAction(person, 'rotate_180')}
                            >
                              Rotate 180
                            </button>
                          </>
                        )}
                        <label className={`person-upload-btn ${updatingFaceForId === person.id ? 'disabled' : ''}`}>
                          Take Photo
                          <input type="file" accept="image/*" capture="environment" disabled={updatingFaceForId === person.id} onChange={(e) => handleUpdatePersonFaceSelect(person, e)} hidden />
                        </label>
                        <label className={`person-upload-btn ${updatingFaceForId === person.id ? 'disabled' : ''}`}>
                          Choose from Gallery
                          <input type="file" accept="image/*" disabled={updatingFaceForId === person.id} onChange={(e) => handleUpdatePersonFaceSelect(person, e)} hidden />
                        </label>
                        {person.profilePhotoPath && (
                          <button
                            type="button"
                            className="person-delete-btn compact-action-btn"
                            disabled={updatingFaceForId === person.id}
                            onClick={() => handleRemovePersonFace(person)}
                          >
                            Remove Picture
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {renderPersonRecordFields(editingPersonData, setEditingPersonData)}
                  {renderAssociateEditor(editingPersonData.knownAssociates || [], (nextAssociates) =>
                    setEditingPersonData((current) => ({
                      ...current,
                      knownAssociates: nextAssociates
                    }))
                  )}
                  <button className="save-btn" onClick={handleSavePersonEdit}>Save Person</button>
                  <button className="cancel-btn" onClick={() => { setEditingPersonId(''); setEditingPersonData(null); }}>Cancel</button>
                </div>
              ) : (
                <>
                  <div><strong>Source:</strong> {sourceLabel}</div>
                  <div><strong>Card Created:</strong> {formatTimestamp(person.createdAt)}</div>
                  <div><strong>Most Recent Interaction:</strong> {formatTimestamp(person.mostRecentInteractionAt)}</div>
                  <div><strong>Age:</strong> {personAge}</div>
                  <div><strong>Race/Sex:</strong> {highlightMatch(person.race || '', search)}/{highlightMatch(person.sex || '', search)}</div>
                  <div><strong>Address:</strong> {highlightMatch([person.address, person.addressCity, person.addressState, person.addressZip].filter(Boolean).join(', '), search)}</div>
                  <div><strong>DL:</strong> {highlightMatch(person.dl || '', search)} {highlightMatch(person.dlState || '', search)}</div>
                  <div><strong>Phone:</strong> {highlightMatch(person.phone || '', search)}</div>
                  {Array.isArray(person.knownAssociates) && person.knownAssociates.length > 0 && (
                    <div>
                      <strong>Known Associates:</strong>
                      <ul className="person-associates-list">
                        {person.knownAssociates.map((associate, index) => (
                          <li key={`${associate}-${index}`}>{highlightMatch(associate, search)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="person-actions">
                    <button onClick={() => { setEditingPersonId(person.id); setEditingPersonData({ ...person }); }}>Edit</button>
                     <div className="note-share-menu-wrap">
                       <button
                         type="button"
                         className={`note-share-trigger${
                           sharingNoteId === person.id ? ' is-sharing' : ''
                         }`}
                         disabled={sharingNoteId === person.id}
                         aria-busy={sharingNoteId === person.id}
                         onClick={() => setShareMenuForId(
                           shareMenuForId === `person-${person.id}` ? '' : `person-${person.id}`
                         )}
                       >
                         {sharingNoteId === person.id && (
                           <span className="note-share-spinner" aria-hidden="true" />
                         )}
                         {sharingNoteId === person.id ? 'Sharing...' : 'Share'}
                       </button>
                      {shareMenuForId === `person-${person.id}` && (
                        <div className="note-share-menu">
                          <button
                            type="button"
                            onClick={() => {
                              setShareMenuForId('');
                              handleWebSharePersonRecord(person);
                            }}
                           >
                             Copy / Send
                           </button>
                           <button
                             type="button"
                             disabled={sharingNoteId === person.id}
                            onClick={() => {
                              setShareMenuForId('');
                              if (person.organizationContribution?.active === true) {
                                 toast.info(
                                   'This person record has already been shared with your organization.'
                                  );
                                  return;
                                }
                                openShareConfirmation(
                                  'organization',
                                  SHARE_CONFIRMATION_MESSAGES.personOrganization,
                                  () => handleSharePersonRecordToOrganization(person)
                                );
                              }}
                           >
                             {person.organizationContribution?.active === true
                               ? 'Organization Shared'
                               : sharingNoteId === person.id
                                 ? 'Sharing...'
                                 : 'Share to Organization'}
                           </button>
                           <button
                             type="button"
                             disabled={sharingNoteId === person.id}
                              onClick={() => {
                                setShareMenuForId('');
                                if (person.globalShare?.active === true) {
                                  handleGlobalPersonRecordShare(person);
                                  return;
                                }
                                openShareConfirmation(
                                  'global',
                                  SHARE_CONFIRMATION_MESSAGES.personGlobal,
                                  () => handleGlobalPersonRecordShare(person)
                                );
                              }}
                           >
                             {person.globalShare?.active === true
                               ? 'Stop Global Share'
                               : sharingNoteId === person.id
                                 ? 'Sharing...'
                                 : 'Share Globally'}
                           </button>
                         </div>
                      )}
                    </div>
                    <button
                      disabled={creatingInteractionForId === person.id}
                      onClick={() => handleAddNewInteraction(person)}
                    >
                      {creatingInteractionForId === person.id ? 'Opening...' : 'Add New Interaction'}
                    </button>
                    <button className="person-delete-btn" onClick={() => handleDeletePersonRecord(person)}>Delete</button>
                  </div>
                  <div className="interaction-history">
                    <strong>Interactions:</strong>
                    {(person.interactions || []).map((interaction) => (
                      <div className="interaction-card" id={`interaction-${interaction.id}`} key={interaction.id}>
                        <button className="timestamp-link" onClick={() => openAddDetails(person, interaction)}>
                          {formatTimestamp(interaction.createdAt)}
                        </button>
                        <div className="note-narrative-display">{interaction.narrative ? highlightMatch(interaction.narrative, search) : 'No narrative entered.'}</div>
                        {Array.isArray(interaction.knownAssociates) && interaction.knownAssociates.length > 0 && (
                          <div><strong>Known Associates:</strong> {highlightMatch(interaction.knownAssociates, search)}</div>
                        )}
                        {Array.isArray(interaction.photoUrls) && interaction.photoUrls.length > 0 && (
                          <div className="person-attachment-row">
                            {interaction.photoUrls.map((url, index) => (
                              <img
                                key={url}
                                src={url}
                                alt={`Person attachment ${index + 1}`}
                                onClick={() => setModalImage(url)}
                              />
                            ))}
                          </div>
                        )}
                        <button
                          className="person-delete-btn"
                          disabled={deletingInteractionId === interaction.id}
                          onClick={() => handleDeleteInteraction(person, interaction)}
                        >
                          {deletingInteractionId === interaction.id ? 'Deleting...' : 'Delete Interaction'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
)}

{(selectedCategory !== 'Person' || !faceSearchPreview) && (
    <div className={`notes-list ${!sidebarVisible ? 'compact' : ''}`}>
      {filteredNotes.filter((note) => note.type === selectedCategory).map((note) => (
        <div
  key={note.id}
  id={`retention-record-note-${note.id}`}
  className="note-preview"
  onClick={(e) => {
    // Prevent closing if editing
    if (editModeId === note.id) return;

    setExpandedNoteId((prev) => (prev === note.id ? null : note.id));
  }}
>

          <strong>{note.type}:</strong>{' '}
          {note.type === 'Person' && (
            <>
              {highlightMatch([note.firstName, note.middleName, note.lastName].filter(Boolean).join(' '), search)}
              {', DOB: '}
              {note.dob ? highlightMatch(note.dob, search) : ''}
              {', '}
              {highlightMatch(note.race || '', search)}/{highlightMatch(note.sex || '', search)}
            </>
          )}
          {note.type === 'Vehicle' && (
            <>
              {highlightMatch([note.make, note.model].filter(Boolean).join(' '), search)}
              {', '}
              {highlightMatch(note.plate || '', search)}
              {', '}
              {highlightMatch(note.color || '', search)}
            </>
          )}
          {note.type === 'Place' && (
            <>
              {highlightMatch(note.address || '', search)}
              {', Owner: '}
              {highlightMatch(note.owner || '', search)}
            </>
          )}

	  {expandedNoteId !== note.id && (
    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#555' }}>▼</span>
  )}

          {renderRetentionNotice({ ...note, recordType: 'note' })}

          {expandedNoteId === note.id && (
            <div className="note-details">
              <div className="note-clean-display">

                {/* PERSON */}
                {note.type === 'Person' && (
                  <>
                    <div><strong>Full Name:</strong> {editModeId === note.id ? (
  <>
    <input
      value={editedNoteData.firstName || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, firstName: e.target.value }))}
      onClick={e => e.stopPropagation()}
      placeholder="First Name"
    />
					<div style={{ height: '8px' }}></div> 
    <input
      value={editedNoteData.middleName || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, middleName: e.target.value }))}
      onClick={e => e.stopPropagation()}
      placeholder="Middle Name"
    />
					<div style={{ height: '8px' }}></div> 
    <input
      value={editedNoteData.lastName || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, lastName: e.target.value }))}
      onClick={e => e.stopPropagation()}
      placeholder="Last Name"
    />
  </>
) : (
  <>
    {highlightMatch(note.firstName, search)}{" "}
    {highlightMatch(note.middleName, search)}{" "}
    {highlightMatch(note.lastName, search)}
  </>
)}</div>


                   <div><strong>DOB:</strong> {editModeId === note.id ? (
  <input
    value={editedNoteData.dob || ''}
    onChange={e =>
      setEditedNoteData(f => ({
        ...f,
        dob: formatDOB(e.target.value)
      }))
    }
    placeholder="MM/DD/YYYY"
    maxLength={10}
  />
) : (
  `${note.dob || 'N/A'} (Age: ${note.age || calculateAge(note.dob)})`
)}</div>


                    <div><strong>Race/Sex:</strong> {editModeId === note.id ? (
                      <>
                        <input value={editedNoteData.race || ''} onChange={e => setEditedNoteData(f => ({ ...f, race: e.target.value }))} placeholder="Race" />

			<div style={{ height: '8px' }}></div> 

                        <input value={editedNoteData.sex || ''} onChange={e => setEditedNoteData(f => ({ ...f, sex: e.target.value }))} placeholder="Sex" />
                      </>
                    ) : `${note.race}/${note.sex}`}</div>

                 <div><strong>Address:</strong> {editModeId === note.id ? (
  <>
    <input
      value={editedNoteData.address || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, address: e.target.value }))}
      placeholder="Street Address"
    />
					<div style={{ height: '8px' }}></div> 
    <input
      value={editedNoteData.addressCity || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, addressCity: e.target.value }))}
      placeholder="City"				
    />
					<div style={{ height: '8px' }}></div> 
    <input
      value={editedNoteData.addressState || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, addressState: e.target.value }))}
      placeholder="State"
    />
					<div style={{ height: '8px' }}></div> 
    <input
      value={editedNoteData.addressZip || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, addressZip: e.target.value }))}
      placeholder="Zip Code"
    />	{/* ADD space between the fields. */}<div style={{ height: '8px' }}></div> 
  </>
) : (
  <>
    {highlightMatch(note.address, search)}, {highlightMatch(note.addressCity, search)}, {highlightMatch(note.addressState, search)} {highlightMatch(note.addressZip, search)}
  </>
)}</div>

                    <div><strong>DL #:</strong> {editModeId === note.id ? (
  <>
    <input
      value={editedNoteData.dl || ''}
      onChange={e => setEditedNoteData(f => ({ ...f, dl: e.target.value }))}
      placeholder="DL #"
    />
  </>
) : (
  highlightMatch(note.dl || "", search)
)}</div>

<div style={{ height: '8px' }}></div>  {/* ADD space between the fields */}

<div><strong>DL State:</strong> {editModeId === note.id ? (
  <input
    value={editedNoteData.dlState || ''}
    onChange={e => setEditedNoteData(f => ({ ...f, dlState: e.target.value }))}
    placeholder="DL State"
  />
) : (
  note.dlState || ""
)}</div>


<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}

                    <div><strong>Phone:</strong> {editModeId === note.id ? (
                      <input value={editedNoteData.phone || ''} onChange={e => setEditedNoteData(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" />
                    ) : note.phone}</div>
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
		<div>
  <strong>Known Associates:</strong>{" "}
  {editModeId === note.id ? (
    <>
      {(editedNoteData.knownAssociates || []).map((assoc, index) => (
        <div key={index} style={{ display: 'flex', marginBottom: '6px' }}>
          <input
            value={assoc}
            placeholder={`Associate #${index + 1}`}
            onChange={e => {
              const updated = [...editedNoteData.knownAssociates];
              updated[index] = e.target.value;
              setEditedNoteData(f => ({ ...f, knownAssociates: updated }));
            }}
            style={{ flex: 1, marginRight: '6px' }}
          />
          <button
            onClick={() => {
              const updated = [...editedNoteData.knownAssociates];
              updated.splice(index, 1);
              setEditedNoteData(f => ({ ...f, knownAssociates: updated }));
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'red',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ✖
          </button>
        </div>
      ))}
      <button
        onClick={() =>
          setEditedNoteData(f => ({
            ...f,
            knownAssociates: [...(f.knownAssociates || []), '']
          }))
        }
        style={{
          padding: '6px 12px',
          border: '1px solid #007bff',
          backgroundColor: '#007bff',
          color: '#fff',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        + Add Associate
      </button>
    </>
  ) : (
    highlightMatch(note.knownAssociates, search)
  )}
</div>


                  <div>
  <strong>Narrative:</strong>{" "}
  {editModeId === note.id ? (
    <textarea
      className="wide-textarea"
      value={editedNoteData.narrative || ''}
      onChange={e =>
        setEditedNoteData(f => ({ ...f, narrative: e.target.value }))
      }
    />
  ) : (
    <span className="note-narrative-display">{highlightMatch(note.narrative, search)}</span>
  )}
</div>

                  </>
                )}

                {/* VEHICLE */}
               {note.type === 'Vehicle' && (
  <>
    <div><strong>Make/Model:</strong> {editModeId === note.id ? (
      <>
        <input
          value={editedNoteData.make || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, make: e.target.value }))}
          placeholder="Make"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
        <input
          value={editedNoteData.model || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, model: e.target.value }))}
          placeholder="Model"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
      </>
    ) : (
      <>
        {highlightMatch(note.make || '', search)} {highlightMatch(note.model || '', search)}
      </>
    )}</div>

    <div><strong>Plate/State:</strong> {editModeId === note.id ? (
      <>
        <input
          value={editedNoteData.plate || ''}
          onChange={e =>
            setEditedNoteData(f => ({ ...f, plate: e.target.value }))
          }
          placeholder="Plate"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
        <input
          value={editedNoteData.plateState || ''}
          onChange={e =>
            setEditedNoteData(f => ({ ...f, plateState: e.target.value }))
          }
          placeholder="State"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
      </>
    ) : (
      <>
        <span style={{ textTransform: 'uppercase' }}>{highlightMatch(note.plate || '', search)}</span> /{" "}
        <span style={{ textTransform: 'uppercase' }}>{highlightMatch(note.plateState || '', search)}</span>
      </>
    )}</div>

    <div><strong>Color:</strong> {editModeId === note.id ? (
      <input
        value={editedNoteData.color || ''}
        onChange={e => setEditedNoteData(f => ({ ...f, color: e.target.value }))}
        placeholder="Color"
      />
    ) : highlightMatch(note.color || '', search)}</div>
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}

    <div><strong>Owner:</strong> {editModeId === note.id ? (
      <input
        value={editedNoteData.owner || ''}
        onChange={e => setEditedNoteData(f => ({ ...f, owner: e.target.value }))}
        placeholder="Owner"
      />
    ) : highlightMatch(note.owner || '', search)}</div>

    <div>
      <strong>Narrative:</strong>{" "}
      {editModeId === note.id ? (
        <textarea
          className="wide-textarea"
          value={editedNoteData.narrative || ''}
          onChange={e =>
            setEditedNoteData(f => ({ ...f, narrative: e.target.value }))
          }
        />
      ) : (
        <span className="note-narrative-display">{highlightMatch(note.narrative, search)}</span>
      )}
    </div>
  </>
)}


                {/* PLACE */}
              {note.type === 'Place' && (
  <>
    <div><strong>Address:</strong> {editModeId === note.id ? (
      <>
        <input
          value={editedNoteData.address || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, address: e.target.value }))}
          placeholder="Street Address"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
        <input
          value={editedNoteData.addressCity || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, addressCity: e.target.value }))}
          placeholder="City"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
        <input
          value={editedNoteData.addressState || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, addressState: e.target.value }))}
          placeholder="State"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
        <input
          value={editedNoteData.addressZip || ''}
          onChange={e => setEditedNoteData(f => ({ ...f, addressZip: e.target.value }))}
          placeholder="ZIP Code"
        />
<div style={{ height: '8px' }}></div>  {/* ADD space between the fields. */}
      </>
    ) : (
      <>
        {highlightMatch(note.address || '', search)}, {highlightMatch(note.addressCity || '', search)}, {highlightMatch(note.addressState || '', search)} {highlightMatch(note.addressZip || '', search)}
      </>
    )}</div>


                    <div><strong>Owner:</strong> {editModeId === note.id ? (
                      <input value={editedNoteData.owner || ''} onChange={e => setEditedNoteData(f => ({ ...f, owner: e.target.value }))} placeholder="Owner" />
                    ) : highlightMatch(note.owner || '', search)}</div>
<div style={{ height: '8px' }}></div>  {/* ADD soace between the fields. */}
                    <div><strong>Occupants:</strong> {editModeId === note.id ? (
                      <>
                        {(editedNoteData.occupants || []).map((occ, i) => (
                          <input key={i} value={occ} placeholder={`Occupant #${i + 1}`} onChange={e => {
                            const updated = [...editedNoteData.occupants];
                            updated[i] = e.target.value;
                            setEditedNoteData(f => ({ ...f, occupants: updated }));
                          }} />
                        ))}
                        <button onClick={() =>
                          setEditedNoteData(f => ({
                            ...f,
                            occupants: [...(f.occupants || []), '']
                          }))
                        }>+ Add Occupant</button>
                      </>
                    ) : highlightMatch(note.occupants || [], search)}</div>

                    <div>
  <strong>Narrative:</strong>{" "}
  {editModeId === note.id ? (
    <textarea
      className="wide-textarea"
      value={editedNoteData.narrative || ''}
      onChange={e =>
        setEditedNoteData(f => ({ ...f, narrative: e.target.value }))
      }
    />
  ) : (
    <span className="note-narrative-display">{highlightMatch(note.narrative, search)}</span>
  )}
</div>

                  </>
                )}

	
	{/* 📸 Attached Photos (with delete option in edit mode) */}
{Array.isArray(note.photoUrls) && note.photoUrls.length > 0 && (
  <div style={{ marginTop: "10px" }}>
    <strong>Attached Photos:</strong>
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginTop: "6px",
      }}
    >
      {(editModeId === note.id ? editedNoteData.photoUrls : note.photoUrls).map((url, i) => (
        <div
          key={i}
          style={{
            width: "100px",
            height: "100px",
            overflow: "visible",
            borderRadius: "6px",
            border: "1px solid #ccc",
            position: "relative",
            flexShrink: 0,
            cursor: "pointer",
          }}
         onClick={(e) => {
 	 e.stopPropagation();
  	setModalImage(url);
	}}
        >
          <img
            src={url}
            alt={`Attachment ${i + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "6px",
              display: "block",
            }}
          />
      {editModeId === note.id && (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();

      // Remove from state; Cloud Functions clean up the Storage object.
      setEditedNoteData((prev) => ({
        ...prev,
        photoUrls: prev.photoUrls.filter((_, idx) => idx !== i),
      }));
    }}
    style={{
      position: "absolute",
      top: "-10px",
      right: "-10px",
      background: "rgba(0,0,0,0.6)",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: "22px",
      height: "22px",
      cursor: "pointer",
      fontWeight: "bold",
      lineHeight: "18px",
      zIndex: 5,
      opacity: 0.8,
      transition: "opacity 0.2s, transform 0.2s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
    title="Remove photo"
  >
    ×
  </button>
)}


        </div>
      ))}
    </div>
  </div>
)}


                {/* TIMESTAMP */}
               {note.timestamp?.seconds && (
  <div>
    <strong>Saved on:</strong> {new Date(note.timestamp.seconds * 1000).toLocaleString()}
  </div>
)}

{note.editedAt && (
  <div style={{ fontSize: '0.9em', color: '#666' }}>
    <strong>Edited:</strong> {new Date(note.editedAt.seconds * 1000).toLocaleString()}
  </div>
)}


  {/* Show new files to be added */}
  {editedPreviewUrls.length > 0 && (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "6px" }}>
      {editedPreviewUrls.map((src, i) => (
        <div key={i} style={{ position: "relative" }}>
          <img
            src={src}
            alt={`New file ${i + 1}`}
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <button
            type="button"
            onClick={() => {
              setEditedFiles((prev) => prev.filter((_, idx) => idx !== i));
              setEditedPreviewUrls((prev) => prev.filter((_, idx) => idx !== i));
            }}
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "rgba(0,0,0,0.6)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              cursor: "pointer",
              fontWeight: "bold",
              lineHeight: "16px",
            }}
            title="Remove"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )}

  
</div>

              <div className="fieldbook-share-badges">
                {note.organizationContribution?.active === true && (
                  <span title="Information from this note has been added to your organization's Fieldbook.">
                    🏢
                  </span>
                )}
                {note.globalShare?.active === true && (
                  <span title="This note is currently shared globally with verified BlueWire users.">
                    🌐
                  </span>
                )}
              </div>

              <div className="note-action-buttons">
                {editModeId === note.id ? (
                  <>
	{/* 📸 Add or Remove Attachments (Edit Mode) */}
<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: "16px" }}>
  <label style={{ fontWeight: "bold", marginBottom: "6px" }}>Attachments:</label>

  <input
    type="file"
    accept="image/*,.pdf,.doc,.docx,.txt"
    multiple
    style={{
      marginBottom: "10px",
      display: "block",
      padding: "6px 0",
    }}
    onChange={(e) => {
      const files = Array.from(e.target.files);
      setEditedFiles((prev) => [...prev, ...files]);
      const newPreviews = files.map((f) => URL.createObjectURL(f));
      setEditedPreviewUrls((prev) => [...prev, ...newPreviews]);
    }}
  />

  {/* 💾 Save and ❌ Cancel buttons below the file picker */}
  <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
    <button
      onClick={handleSaveInlineEdit}
      disabled={isSavingNote}
      style={{
        padding: "6px 12px",
        border: "1px solid #007bff",
        backgroundColor: "#007bff",
        color: "white",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      {isSavingNote ? "Saving..." : "💾 Save"}
    </button>

    <button
      disabled={isSavingNote}
      onClick={() => {
        setEditModeId(null);
        setEditedNoteData(null);
      }}
      style={{
        padding: "6px 12px",
        border: "1px solid #ccc",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      ❌ Cancel
    </button>
  </div>
</div>

                  </>
                ) : (
                  <>
                    

		  <button onClick={(e) => { e.stopPropagation(); handleEditNote(note); }}>✏️ Edit</button>

          <div className="note-share-menu-wrap">
            <button
              type="button"
              className={`note-share-trigger${
                sharingNoteId === note.id ? ' is-sharing' : ''
              }`}
              disabled={sharingNoteId === note.id}
              aria-busy={sharingNoteId === note.id}
              onClick={(e) => {
                e.stopPropagation();
                setShareMenuForId(shareMenuForId === `note-${note.id}` ? '' : `note-${note.id}`);
              }}
            >
              {sharingNoteId === note.id && (
                <span className="note-share-spinner" aria-hidden="true" />
              )}
              {sharingNoteId === note.id ? 'Sharing...' : 'Share'}
            </button>
            {shareMenuForId === `note-${note.id}` && (
              <div className="note-share-menu" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => {
                    setShareMenuForId('');
                    handleWebShareNote(note);
                  }}
                >
                  Copy / Send
                </button>
                <button
                  type="button"
                  disabled={sharingNoteId === note.id}
                    onClick={() => {
                      setShareMenuForId('');
                      if (note.organizationContribution?.active === true) {
                      toast.info(
                        'This note has already been shared with your organization.'
                        );
                        return;
                      }
                    openShareConfirmation(
                      'organization',
                      SHARE_CONFIRMATION_MESSAGES.noteOrganization,
                      () => handleShareNote(note)
                    );
                  }}
                >
                  {note.organizationContribution?.active === true
                    ? "Organization Shared"
                    : sharingNoteId === note.id
                      ? "Sharing..."
                      : "Share to Organization"}
                </button>
                {String(note.type || '').toLowerCase() !== 'place' && (
                  <button
                    type="button"
                    disabled={sharingNoteId === note.id}
                    onClick={() => {
                      setShareMenuForId('');
                      if (note.globalShare?.active === true) {
                        handleGlobalFieldbookShare(note);
                        return;
                      }
                      openShareConfirmation(
                        'global',
                        SHARE_CONFIRMATION_MESSAGES.noteGlobal,
                        () => handleGlobalFieldbookShare(note)
                      );
                    }}
                  >
                    {note.globalShare?.active === true
                      ? "Stop Global Share"
                      : sharingNoteId === note.id
                        ? "Sharing..."
                        : "Share Globally"}
                  </button>
                )}
              </div>
            )}
          </div>
                  <button
                    disabled={Boolean(deletingNoteId)}
                    onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id)}}
                  >
                    {deletingNoteId === note.id ? "Deleting..." : "🗑️ Delete"}
                  </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
)}

    {!modalVisible && !editModalVisible && !personModalVisible && !detailsPersonId && !mergeModalVisible && (
      <button className="add-note-btn" onClick={openAddNote}>+ Add New Note</button>
    )}

    {shareConfirmation && (
      <ShareConfirmationModal
        confirmation={shareConfirmation}
        pending={isShareConfirmationPending}
        onCancel={cancelShareConfirmation}
        onConfirm={confirmShareConfirmation}
        cancelButtonRef={shareConfirmationCancelRef}
      />
    )}

  {retentionWarningRecord && (
    <div className="note-modal retention-modal">
      <div className="note-form retention-modal-card">
        <h3>Note Expiration Warning</h3>
        <p>
          {getRetentionRecordLabel(retentionWarningRecord)} is scheduled for deletion in{' '}
          <strong>{getRetentionDaysRemaining(retentionWarningRecord)} days</strong>.
        </p>
        <p>
          Open the note to review the countdown or extend it for another three months.
        </p>
        <div className="note-action-buttons">
          <button
            type="button"
            className="cancel-btn"
            disabled={isUpdatingRetention}
            onClick={() => acknowledgeRetentionWarning(false)}
          >
            OK
          </button>
          <button
            type="button"
            className="save-btn"
            disabled={isUpdatingRetention}
            onClick={() => acknowledgeRetentionWarning(true)}
          >
            Go to Note
          </button>
        </div>
      </div>
    </div>
  )}

  {retentionExtendRecord && (
    <div className="note-modal retention-modal">
      <div className="note-form retention-modal-card">
        <h3>Extend This Note?</h3>
        <p>Extend this note for another three months?</p>
        <div className="note-action-buttons">
          <button
            type="button"
            className="cancel-btn"
            disabled={isUpdatingRetention}
            onClick={() => setRetentionExtendRecord(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="save-btn"
            disabled={isUpdatingRetention}
            onClick={confirmExtendRetention}
          >
            {isUpdatingRetention ? 'Extending...' : 'Extend'}
          </button>
        </div>
      </div>
    </div>
  )}

  {modalVisible && (
  <div className="note-modal">
    <div className="note-form">
      {addNoteStep === 'chooseType' ? (
        <>
          <h3>Add New Note</h3>
          <div className="note-type-tile-grid">
            <button onClick={() => chooseAddNoteType('Person')}>Person</button>
            <button onClick={() => chooseAddNoteType('Vehicle')}>Vehicle</button>
            <button onClick={() => chooseAddNoteType('Place')}>Place</button>
          </div>
          <div className="note-action-buttons">
            <button className="cancel-btn" onClick={() => setModalVisible(false)}>Cancel</button>
          </div>
        </>
      ) : (
      <>
      <div className="note-tabs">
        <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
  <button
    style={{
      margin: '0 8px',
      padding: '8px 16px',
      borderRadius: '6px',
      border: tab === 'Person' ? '2px solid #007bff' : '1px solid #ccc',
      backgroundColor: tab === 'Person' ? '#e7f1ff' : '#f8f9fa',
      cursor: 'pointer'
    }}
    onClick={() => handleTabChange('Person')}
  >
    Person
  </button>

  <button
    style={{
      margin: '0 8px',
      padding: '8px 16px',
      borderRadius: '6px',
      border: tab === 'Vehicle' ? '2px solid #007bff' : '1px solid #ccc',
      backgroundColor: tab === 'Vehicle' ? '#e7f1ff' : '#f8f9fa',
      cursor: 'pointer'
    }}
    onClick={() => handleTabChange('Vehicle')}
  >
    Vehicle
  </button>

  <button
    style={{
      margin: '0 8px',
      padding: '8px 16px',
      borderRadius: '6px',
      border: tab === 'Place' ? '2px solid #007bff' : '1px solid #ccc',
      backgroundColor: tab === 'Place' ? '#e7f1ff' : '#f8f9fa',
      cursor: 'pointer'
    }}
    onClick={() => handleTabChange('Place')}
  >
    Place
  </button>
</div>

	 {tab === 'Person' ? (
      <>
        <p className="person-notice">Create a person record. Add a picture only if you want this person to be searchable by facial search.</p>
        {!showFacialSearchOptions ? (
          <div className="person-facial-search-start">
            <button className="person-secondary-btn" type="button" onClick={() => setShowFacialSearchOptions(true)}>
              Add Picture for Facial Search
            </button>
          </div>
        ) : (
          personCreateStep !== 'image' && (
            <div className="person-method-grid">
              <button type="button" onClick={() => handlePersonMethod('person_photo')}>Add by Person Photograph</button>
              <button type="button" onClick={() => handlePersonMethod('scanned_id')}>Add by ID</button>
            </div>
          )
        )}
        {personCreateStep === 'image' && (
          <div className="person-image-step">
            <div className="person-method-grid person-image-actions">
              <label className="person-upload-btn">
                <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
                <span>Take Photo</span>
                <input type="file" accept="image/*" capture="environment" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
              </label>
              <label className="person-upload-btn">
                <FontAwesomeIcon icon={faImages} aria-hidden="true" />
                <span>Choose from Gallery</span>
                <input type="file" accept="image/*" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
              </label>
            </div>
            {isProcessingPersonImage && <div className="face-search-message">Processing image...</div>}
            <button type="button" className="cancel-btn" onClick={() => setPersonCreateStep('review')}>Back</button>
          </div>
        )}
        {personCreateStep === 'selectPortrait' && (
          <div className="id-portrait-selection">
            <p className="person-notice">Select the main ID portrait for facial search.</p>
            <div className="id-portrait-grid">
              {idPortraitCandidates.map((candidate, index) => (
                <button
                  type="button"
                  className="id-portrait-option"
                  key={candidate.id || candidate.tempPortraitPath}
                  onClick={() => handleSelectIdPortraitCandidate(candidate)}
                >
                  <img src={candidate.previewUrl} alt={`ID portrait option ${index + 1}`} />
                  <span>Use Portrait {index + 1}</span>
                </button>
              ))}
            </div>
            <button type="button" className="cancel-btn" onClick={handleBackFromIdPortraitSelection}>Back</button>
          </div>
        )}
        {personProfilePreview && (
          <div className="person-profile-manage">
            <img className="person-profile-preview" src={personProfilePreview} alt="Facial search profile" />
            <div className="portrait-review-actions">
              <button
                type="button"
                className="person-secondary-btn compact-action-btn"
                disabled={isProcessingPersonImage}
                onClick={() => handleAdjustPersonTempPortrait('rotate_left')}
              >
                Rotate Left
              </button>
              <button
                type="button"
                className="person-secondary-btn compact-action-btn"
                disabled={isProcessingPersonImage}
                onClick={() => handleAdjustPersonTempPortrait('rotate_right')}
              >
                Rotate Right
              </button>
              <button
                type="button"
                className="person-secondary-btn compact-action-btn"
                disabled={isProcessingPersonImage}
                onClick={() => handleAdjustPersonTempPortrait('rotate_180')}
              >
                Rotate 180
              </button>
              <label className={`person-upload-btn ${isProcessingPersonImage ? 'disabled' : ''}`}>
                Replace
                <input type="file" accept="image/*" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
              </label>
              <button
                type="button"
                className="person-delete-btn compact-action-btn"
                disabled={isProcessingPersonImage}
                onClick={() => handleAdjustPersonTempPortrait('remove')}
              >
                Remove
              </button>
              {isProcessingPersonImage && <span className="face-search-message face-update-message">Updating picture...</span>}
            </div>
          </div>
        )}
        <div className="person-edit-grid">
          {renderPersonRecordFields(personRecordForm, setPersonRecordForm)}
          {renderAssociateEditor(personRecordForm.knownAssociates || [], (nextAssociates) =>
            setPersonRecordForm((current) => ({
              ...current,
              knownAssociates: nextAssociates
            }))
          )}
          <textarea className="note-textarea full-width person-narrative" placeholder="Narrative" rows="7" value={personNarrativeDraft} onChange={(e) => setPersonNarrativeDraft(e.target.value)} />
        </div>
      </>
    ) : renderTabFields()}
      </div>
     {/* 👇 New photo picker */}
<div style={{ marginTop: "16px" }}>
  <label><strong>{tab === 'Person' ? 'Ordinary Photos:' : 'Attach Photos:'}</strong></label>
  <div className="person-method-grid">
  <label className="person-upload-btn">
    Take Photo
    <input
      type="file"
      accept="image/*"
      capture="environment"
      multiple
      hidden
      onChange={(e) => {
  const incoming = Array.from(e.target.files);
  const files = tab === 'Person' ? incoming.slice(0, Math.max(0, 3 - selectedFiles.length)) : incoming;
  if (tab === 'Person' && incoming.length > files.length) {
    toast.info('Person records can include up to three ordinary photos.');
  }
  setSelectedFiles((prev) => tab === 'Person' ? [...prev, ...files].slice(0, 3) : [...prev, ...files]);

  const newPreviews = files.map((f) => URL.createObjectURL(f));
  setPreviewUrls((prev) => tab === 'Person' ? [...prev, ...newPreviews].slice(0, 3) : [...prev, ...newPreviews]);
}}
    />
  </label>
  <label className="person-upload-btn">
    Choose from Gallery
    <input
      type="file"
      accept="image/*,.pdf,.doc,.docx,.txt"
      multiple
      hidden
      onChange={(e) => {
  const incoming = Array.from(e.target.files);
  const files = tab === 'Person' ? incoming.slice(0, Math.max(0, 3 - selectedFiles.length)) : incoming;
  if (tab === 'Person' && incoming.length > files.length) {
    toast.info('Person records can include up to three ordinary photos.');
  }
  setSelectedFiles((prev) => tab === 'Person' ? [...prev, ...files].slice(0, 3) : [...prev, ...files]);

  const newPreviews = files.map((f) => URL.createObjectURL(f));
  setPreviewUrls((prev) => tab === 'Person' ? [...prev, ...newPreviews].slice(0, 3) : [...prev, ...newPreviews]);
}}
    />
  </label>
  </div>
</div>

	{previewUrls.length > 0 && (
  <div style={{ marginTop: "12px" }}>
    <strong>Selected Photos Preview:</strong>
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        marginTop: "8px",
      }}
    >
      {previewUrls.map((src, i) => (
        <div
          key={i}
          style={{
            position: "relative",
            display: "inline-block",
          }}
        >
          <img
            src={src}
            alt={`Preview ${i + 1}`}
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />

          {/* ❌ Remove button */}
          <button
            type="button"
            onClick={() => {
              setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i));
              setPreviewUrls((prev) => prev.filter((_, idx) => idx !== i));
            }}
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "rgba(0,0,0,0.6)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              cursor: "pointer",
              fontWeight: "bold",
              lineHeight: "16px",
            }}
            title="Remove"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  </div>
)}



<div className="note-action-buttons">
  {tab === 'Person' ? (
    <button className="save-btn" onClick={handleSavePersonRecord} disabled={isSavingPerson}>
      {isSavingPerson ? "Saving..." : "Approve & Save"}
    </button>
  ) : (
    <button className="save-btn" onClick={handleAddNote} disabled={isCreatingNote}>
      {isCreatingNote ? "Saving..." : "Save"}
    </button>
  )}
  <button className="cancel-btn" onClick={() => setModalVisible(false)} disabled={isCreatingNote}>❌ Cancel</button>
</div>


      </>
      )}
            </div>
          </div>
	 )}
	<div style={{ height: '80px' }}></div>  {/* 👈 Spacer to allow scrolling */}

  {personModalVisible && (
    <div className="note-modal">
      <div className="note-form person-record-form">
        <h3>Add Person</h3>
        <p className="person-notice">Search your existing notes for this person before adding a new entry.</p>
        {personCreateStep === 'method' && (
          <div className="person-method-grid">
            <button onClick={() => handlePersonMethod('person_photo')}>Add by person photograph</button>
            <button onClick={() => handlePersonMethod('scanned_id')}>Add by ID</button>
          </div>
        )}
        {personCreateStep === 'image' && (
          <div className="person-image-step">
            <div className="person-method-grid person-image-actions">
              <label className={`person-upload-btn ${isProcessingPersonImage ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
                <span>{isProcessingPersonImage ? 'Processing...' : 'Take Photo'}</span>
                <input type="file" accept="image/*" capture="environment" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
              </label>
              <label className={`person-upload-btn ${isProcessingPersonImage ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={faImages} aria-hidden="true" />
                <span>Choose from Gallery</span>
                <input type="file" accept="image/*" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
              </label>
            </div>
            {personProfilePreview && <img className="person-profile-preview" src={personProfilePreview} alt="Selected person" />}
            <button className="cancel-btn" onClick={() => setPersonCreateStep('method')}>Back</button>
          </div>
        )}
        {personCreateStep === 'selectPortrait' && (
          <div className="id-portrait-selection">
            <p className="person-notice">Select the main ID portrait for facial search.</p>
            <div className="id-portrait-grid">
              {idPortraitCandidates.map((candidate, index) => (
                <button
                  type="button"
                  className="id-portrait-option"
                  key={candidate.id || candidate.tempPortraitPath}
                  onClick={() => handleSelectIdPortraitCandidate(candidate)}
                >
                  <img src={candidate.previewUrl} alt={`ID portrait option ${index + 1}`} />
                  <span>Use Portrait {index + 1}</span>
                </button>
              ))}
            </div>
            <button className="cancel-btn" onClick={handleBackFromIdPortraitSelection}>Back</button>
          </div>
        )}
        {personCreateStep === 'review' && (
          <>
            {personProfilePreview && <img className="person-profile-preview" src={personProfilePreview} alt="Profile face confirmation" />}
            {personProfilePreview && (
              <div className="portrait-review-actions">
                <button
                  type="button"
                  className="person-secondary-btn compact-action-btn"
                  disabled={isProcessingPersonImage}
                  onClick={() => handleAdjustPersonTempPortrait('rotate_left')}
                >
                  Rotate Left
                </button>
                <button
                  type="button"
                  className="person-secondary-btn compact-action-btn"
                  disabled={isProcessingPersonImage}
                  onClick={() => handleAdjustPersonTempPortrait('rotate_right')}
                >
                  Rotate Right
                </button>
                <button
                  type="button"
                  className="person-secondary-btn compact-action-btn"
                  disabled={isProcessingPersonImage}
                  onClick={() => handleAdjustPersonTempPortrait('rotate_180')}
                >
                  Rotate 180
                </button>
                <label className={`person-upload-btn ${isProcessingPersonImage ? 'disabled' : ''}`}>
                  Replace
                  <input type="file" accept="image/*" disabled={isProcessingPersonImage} onChange={handlePersonImageSelect} hidden />
                </label>
                <button
                  type="button"
                  className="person-delete-btn compact-action-btn"
                  disabled={isProcessingPersonImage}
                  onClick={() => handleAdjustPersonTempPortrait('remove')}
                >
                  Remove
                </button>
                {isProcessingPersonImage && <span className="face-search-message face-update-message">Updating picture...</span>}
              </div>
            )}
            <div className="person-edit-grid">
              {renderPersonRecordFields(personRecordForm, setPersonRecordForm)}
              {renderAssociateEditor(personRecordForm.knownAssociates || [], (nextAssociates) =>
                setPersonRecordForm((current) => ({
                  ...current,
                  knownAssociates: nextAssociates
                }))
              )}
              <textarea className="note-textarea full-width" placeholder="Narrative" rows="5" value={personNarrativeDraft} onChange={(e) => setPersonNarrativeDraft(e.target.value)} />
            </div>
            <div className="note-action-buttons">
              <button className="save-btn" onClick={handleSavePersonRecord} disabled={isSavingPerson}>{isSavingPerson ? 'Saving...' : 'Approve & Save'}</button>
              <button className="cancel-btn" onClick={() => setPersonModalVisible(false)} disabled={isSavingPerson}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  )}

  {detailsPersonId && (
    <div className="note-modal">
      <div className="note-form person-record-form">
        <h3>Add Details</h3>
        <textarea className="note-textarea full-width" rows="8" value={detailsNarrative} onChange={(e) => setDetailsNarrative(e.target.value)} placeholder="Interaction narrative" />
        <div className="details-attachments-editor">
          <strong>Picture Attachments:</strong>
          {detailsPhotoUrls.length > 0 && (
            <div className="person-attachment-row">
              {detailsPhotoUrls.map((url, index) => (
                <div className="attachment-edit-thumb" key={`${url}-${index}`}>
                  <img
                    src={url}
                    alt={`Interaction attachment ${index + 1}`}
                    onClick={() => setModalImage(url)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setDetailsPhotoUrls((current) => current.filter((_, idx) => idx !== index));
                      setDetailsPhotoPaths((current) => current.filter((_, idx) => idx !== index));
                    }}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
          {detailsPreviewUrls.length > 0 && (
            <div className="person-attachment-row">
              {detailsPreviewUrls.map((src, index) => (
                <div className="attachment-edit-thumb" key={`${src}-${index}`}>
                  <img src={src} alt={`New interaction attachment ${index + 1}`} />
                  <button
                    type="button"
                    onClick={() => {
                      URL.revokeObjectURL(src);
                      setDetailsFiles((current) => current.filter((_, idx) => idx !== index));
                      setDetailsPreviewUrls((current) => current.filter((_, idx) => idx !== index));
                    }}
                    aria-label="Remove new attachment"
                    title="Remove new attachment"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="person-method-grid">
            <label className="person-upload-btn">
              Take Photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                hidden
                disabled={isSavingDetails}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setDetailsFiles((current) => [...current, ...files]);
                  setDetailsPreviewUrls((current) => [
                    ...current,
                    ...files.map((file) => URL.createObjectURL(file))
                  ]);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="person-upload-btn">
              Choose from Gallery
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                disabled={isSavingDetails}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setDetailsFiles((current) => [...current, ...files]);
                  setDetailsPreviewUrls((current) => [
                    ...current,
                    ...files.map((file) => URL.createObjectURL(file))
                  ]);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>
        <div className="interaction-associates-editor">
          <strong>Known Associates:</strong>
          {detailsAssociates.map((assoc, index) => (
            <div key={index} className="associate-row">
              <input
                value={assoc}
                placeholder={`Associate #${index + 1}`}
                onChange={(e) => {
                  const updated = [...detailsAssociates];
                  updated[index] = e.target.value;
                  setDetailsAssociates(updated);
                }}
              />
              <button
                type="button"
                onClick={() => setDetailsAssociates((current) => current.filter((_, idx) => idx !== index))}
              >
                x
              </button>
            </div>
          ))}
          <button
            type="button"
            className="add-associate-btn"
            onClick={() => setDetailsAssociates((current) => [...current, ''])}
          >
            + Add Associate
          </button>
        </div>
        <div className="note-action-buttons">
          <button className="save-btn" onClick={saveInteractionDetails} disabled={isSavingDetails}>
            {isSavingDetails ? 'Saving...' : 'Save Details'}
          </button>
          <button className="cancel-btn" onClick={resetDetailsModal} disabled={isSavingDetails}>Cancel</button>
        </div>
      </div>
    </div>
  )}

  {mergeModalVisible && (
    <div className="note-modal merge-person-modal">
      <div className="note-form person-record-form merge-person-form">
        <div className="merge-person-header">
          <h3>Merge Person Records</h3>
          <p>Use the dropdowns to choose which information to keep for the merged person record.</p>
        </div>
        <label>
          Keep Record
          <select value={mergeSurvivorId} onChange={(e) => setMergeSurvivorId(e.target.value)}>
            {mergeCandidates.map((person) => <option key={person.id} value={person.id}>{getPersonName(person)}</option>)}
          </select>
        </label>
        <div className="person-edit-grid">
          {Object.keys(EMPTY_PERSON_RECORD).map((field) => {
            const values = Array.from(new Set(mergeCandidates.map((person) => person[field]).filter(Boolean)));
            return (
              <label key={field}>
                {field}
                <select value={values.includes(mergeSelections[field]) ? mergeSelections[field] : '__other__'} onChange={(e) => setMergeSelections((current) => ({ ...current, [field]: e.target.value === '__other__' ? '' : e.target.value }))}>
                  {values.map((value) => <option key={value} value={value}>{value}</option>)}
                  <option value="__other__">Other</option>
                </select>
                {!values.includes(mergeSelections[field]) && <input value={mergeSelections[field] || ''} placeholder={`Enter ${field}`} onChange={(e) => setMergeSelections((current) => ({ ...current, [field]: e.target.value }))} />}
              </label>
            );
          })}
        </div>
        {mergeKnownAssociates.length > 0 && (
          <div className="merge-associates-review">
            <strong>Known Associates to keep:</strong>
            {mergeKnownAssociates.map((associate, index) => (
              <div key={`${associate}-${index}`} className="associate-row">
                <span>{associate}</span>
                <button
                  type="button"
                  onClick={() => setMergeKnownAssociates((current) => current.filter((_, idx) => idx !== index))}
                  aria-label={`Remove ${associate}`}
                  title="Remove associate"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="note-action-buttons">
          <button className="save-btn" onClick={confirmMerge} disabled={isMerging}>
            {isMerging ? 'Merging...' : 'Merge'}
          </button>
          <button className="cancel-btn" onClick={() => setMergeModalVisible(false)} disabled={isMerging}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )}

	{modalImage && (
  <div
    className="image-preview-overlay"
    onClick={() => setModalImage(null)}
  >
    <div
      className="image-preview-container"
      onClick={(e) => e.stopPropagation()}
    >
      <img src={modalImage} alt="Preview" className="image-preview-full" />
      <button
        className="download-btn"
        onClick={async () => {
          try {
            const result = await saveToGallery(modalImage, { fileName: `note_${Date.now()}.jpg` });
            alert(formatSaveMessage(result));
          } catch (err) {
            console.error('Save failed:', err);
            alert('Error: ' + (err.message || JSON.stringify(err)));
          }
        }}
      >
        ⬇ Save
      </button>
      <button className="close-btn" onClick={() => setModalImage(null)}>
        ✖
      </button>
   			 </div>
  		      </div>
		     )}
		<ToastContainer />

      </div>
    );
  }
